import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  LogOut, Users, Search, Download, Eye, ArrowLeft, AlertTriangle,
  ClipboardList, ClipboardCheck, CheckCircle, User, Phone, Mail, Stethoscope, Calendar,
  CalendarDays, Heart, Brain, Star, FileText, Bus, Waves, Utensils, Droplets, ExternalLink, Save, X,
  Sparkles, Printer, ChevronLeft, ChevronRight, Send, MessageSquare, Trash2, RefreshCw, Clock, Flag, ChevronDown, ChevronUp, Trophy, Swords, BarChart3, LogIn, Shirt, Moon, Edit, KeyRound
} from "lucide-react";
import { NavigatorIntramuralsLanding } from "./NavigatorIntramurals";
import { TeamSorterView, ScorecardView, PlayoffsView, FinalStandingsView } from "./AthleticsDashboard";
import SchedulesPicker from "./SchedulesPicker";
import LoginsView from "./LoginsView";
import MissingLoginAlert from "./MissingLoginAlert";
import ScheduleNotificationsBanner from "./ScheduleNotificationsBanner";
import ThreadedMessagesInbox from "./ThreadedMessagesInbox";
import TripRostersView from "../components/TripRostersView";
import CamperPhoto from "../components/CamperPhoto";
import SupervisorPasswordResetModal from "../components/SupervisorPasswordResetModal";
import MenuSearchBar from "../components/MenuSearchBar";
import ReorderableGrid from "../components/ReorderableGrid";
import SupervisorGroupLogins from "../components/SupervisorGroupLogins";

function decodeJwtPayload(token) {
  try {
    const part = (token || "").split(".")[1] || "";
    const padded = part + "=".repeat((4 - (part.length % 4)) % 4);
    const json = atob(padded.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json);
  } catch { return null; }
}

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const LOGO_URL = `${process.env.REACT_APP_BACKEND_URL}/api/assets/rrdc-logo.png`;

// Division group patterns
const DIVISION_PATTERNS = {
  mariners: /^[BG](0[1-9]|1[01])$/,
  skippers: /^N(0[1-9]|1[0-6])$/,
  navigators: /^[BG]1[2-5]$/,
};

function formatPhone(p) {
  if (!p) return "";
  const d = p.replace(/\D/g, "");
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length === 11 && d[0] === "1") return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  return p;
}

const SESSION_SHORT_LABELS = {
  "Full Season- 5 Days": "FS5", "Half Season 1- 5 Days": "HS1", "Half Season 2- 5 Days": "HS2",
  "6 Week Season": "6W", "Flex 20 Days": "F20", "Flex 25 Days": "F25", "Flex 30 Days": "F30",
};

export default function SupervisorDashboard() {
  const { user, logout } = useAuth();
  const [currentView, setCurrentView] = useState("menu");
  const [campers, setCampers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [camperSearch, setCamperSearch] = useState("");
  const [groupFilter, setGroupFilter] = useState("");
  const [camperSubView, setCamperSubView] = useState("groups"); // "groups" | "list"
  const [selectedCamper, setSelectedCamper] = useState(null);
  const [incidentReports, setIncidentReports] = useState([]);
  const [incidentSearch, setIncidentSearch] = useState("");
  const [incidentDateFilter, setIncidentDateFilter] = useState("");
  const incidentFilterActive = incidentSearch.trim() !== "" || incidentDateFilter !== "";
  const filteredIncidents = incidentReports.filter((r) => {
    const q = incidentSearch.trim().toLowerCase();
    const nameOk = !q || (r.camper_names || []).join(" ").toLowerCase().includes(q) || (r.group_code || "").toLowerCase().includes(q);
    const dateOk = !incidentDateFilter || r.date === incidentDateFilter;
    return nameOk && dateOk;
  });
  const [buddySheets, setBuddySheets] = useState([]);
  const [viewingSheet, setViewingSheet] = useState(null);
  const [tripForms, setTripForms] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([new Date().getFullYear()]);
  const [rosterSubView, setRosterSubView] = useState("groups");
  const [rosterGroupFilter, setRosterGroupFilter] = useState("");
  const [rosterGroupSearch, setRosterGroupSearch] = useState("");
  const [rosterCamperSearch, setRosterCamperSearch] = useState("");
  const [rosterData, setRosterData] = useState(null);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [rosterSelectedDate, setRosterSelectedDate] = useState("");
  const [hobbiesWeek, setHobbiesWeek] = useState(1);
  const [hobbiesReport, setHobbiesReport] = useState(null);
  const [hobbiesHistory, setHobbiesHistory] = useState([]);
  const [hobbiesLoading, setHobbiesLoading] = useState(false);
  const [hobbiesSubView, setHobbiesSubView] = useState("weekly"); // "weekly" | "history"
  const [hobbiesHistoryFilter, setHobbiesHistoryFilter] = useState("all"); // "all" | "boys" | "girls"
  const [hobbiesStats, setHobbiesStats] = useState(null);
  const [attendanceConflicts, setAttendanceConflicts] = useState([]);
  const [attendanceConflictsDate, setAttendanceConflictsDate] = useState("");
  const [resolvingConflict, setResolvingConflict] = useState(null);
  const [tripRostersData, setTripRostersData] = useState(null);
  const [tripRostersLoading, setTripRostersLoading] = useState(false);
  // Specialty supervisor — specialist incident reports
  const [specialistReports, setSpecialistReports] = useState([]);
  const [specialistReportsLoading, setSpecialistReportsLoading] = useState(false);
  const [specialistUnread, setSpecialistUnread] = useState(0);
  const [editingReport, setEditingReport] = useState(null);

  // Messages state
  const [msgGroups, setMsgGroups] = useState([]);
  const [msgSelectedGroup, setMsgSelectedGroup] = useState(null);
  const [msgMessages, setMsgMessages] = useState([]);
  const [msgLoading, setMsgLoading] = useState(false);
  const [msgCamperFilter, setMsgCamperFilter] = useState("");
  const [msgDateFrom, setMsgDateFrom] = useState("");
  const [msgDateTo, setMsgDateTo] = useState("");
  const [msgText, setMsgText] = useState("");
  const [msgChannel, setMsgChannel] = useState("sms");
  const [msgSending, setMsgSending] = useState(false);
  const [msgSelectedCamperIds, setMsgSelectedCamperIds] = useState(new Set());

  const isCurrentYear = selectedYear === new Date().getFullYear();

  // Parse division from JWT token
  const token = localStorage.getItem("token");
  let division = "mariners";
  let divisionName = "Mariners";
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    division = payload.division || "mariners";
    divisionName = payload.division_name || "Mariners";
  } catch (e) {}

  const isMariners = division === "mariners";
  const isNavigators = division === "navigators";
  const hobbiesProgram = isMariners ? "mariner" : isNavigators ? "navigator" : null;
  const [activeReportProgram, setActiveReportProgram] = useState(hobbiesProgram);
  const pattern = DIVISION_PATTERNS[division];

  const filterByDivision = useCallback((items, codeField = "group_code") => {
    if (!pattern) return items;
    return items.filter(item => pattern.test(item[codeField] || ""));
  }, [pattern]);

  const fetchYears = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sessions/years`, { headers: { Authorization: `Bearer ${token}` } });
      setAvailableYears(res.data.years || [new Date().getFullYear()]);
    } catch { /* non-critical */ }
  }, [token]);

  const fetchData = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch all campers + groups, then filter client-side
      const [campersRes, groupsRes] = await Promise.all([
        axios.get(`${API}/campers`, { headers, params: { year: selectedYear } }),
        axios.get(`${API}/groups`, { headers }),
      ]);

      const divGroups = groupsRes.data.filter(g => pattern.test(g.code || ""));
      const divCampers = campersRes.data.filter(c => pattern.test(c.group_code || ""));
      setGroups(divGroups);
      setCampers(divCampers);

      // Fetch incident reports for division groups
      const incRes = await axios.get(`${API}/incident-reports`, { headers, params: { year: selectedYear } });
      setIncidentReports(incRes.data.filter(r => pattern.test(r.group_code || "")));

      // Fetch buddy sheets (use archive endpoint which returns all buddy sheets)
      const bsRes = await axios.get(`${API}/buddy-sheets/archive`, { headers, params: { year: selectedYear } });
      setBuddySheets(bsRes.data.filter(b => pattern.test(b.group_code || "")));

      // Fetch trip forms
      const tfRes = await axios.get(`${API}/trip-forms`, { headers, params: { year: selectedYear } });
      setTripForms(tfRes.data.filter(f => pattern.test(f.group_code || "")));

      // Fetch attendance conflicts (today)
      try {
        const acRes = await axios.get(`${API}/attendance/conflicts`, { headers });
        const filtered = (acRes.data?.conflicts || []).filter(c => pattern.test(c.group_code || ""));
        setAttendanceConflicts(filtered);
        setAttendanceConflictsDate(acRes.data?.date || "");
      } catch (e) { /* ignore — will retry on view open */ }

    } catch (err) {
      console.error("Fetch error:", err);
    }
  }, [token, pattern, selectedYear]);

  useEffect(() => { fetchYears(); }, [fetchYears]);
  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    if (division !== "specialty") return;
    const tk = localStorage.getItem("token");
    axios.get(`${API}/specialist/incident-reports/unread-count`, { headers: { Authorization: `Bearer ${tk}` } })
      .then(r => setSpecialistUnread(r.data?.unread_count || 0))
      .catch(() => {});
    const id = setInterval(() => {
      axios.get(`${API}/specialist/incident-reports/unread-count`, { headers: { Authorization: `Bearer ${tk}` } })
        .then(r => setSpecialistUnread(r.data?.unread_count || 0))
        .catch(() => {});
    }, 30000);
    return () => clearInterval(id);
  }, [division]);

  const fetchRosterForGroup = useCallback(async (groupCode, date) => {
    setRosterLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const params = { group_code: groupCode };
      if (date) params.date = date;
      const res = await axios.get(`${API}/roster/today`, { headers, params });
      setRosterData(res.data);
    } catch {
      toast.error("Failed to load roster");
    } finally {
      setRosterLoading(false);
    }
  }, [token]);

  const fetchHobbiesReport = useCallback(async (weekNum, programOverride) => {
    setHobbiesLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const prog = programOverride || activeReportProgram || hobbiesProgram;
      const res = await axios.get(`${API}/hobbies/report/${weekNum}`, { headers, params: { program: prog } });
      setHobbiesReport(res.data);
    } catch {
      toast.error("Failed to load hobbies report");
    } finally {
      setHobbiesLoading(false);
    }
  }, [token, activeReportProgram, hobbiesProgram]);

  const fetchHobbiesHistory = useCallback(async (div, programOverride) => {
    setHobbiesLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const prog = programOverride || activeReportProgram || hobbiesProgram;
      const params = { program: prog };
      if (div && div !== "all") params.division = div;
      const res = await axios.get(`${API}/hobbies/history`, { headers, params });
      setHobbiesHistory(res.data);
    } catch {
      toast.error("Failed to load hobbies history");
    } finally {
      setHobbiesLoading(false);
    }
  }, [token, activeReportProgram, hobbiesProgram]);

  const fetchHobbiesStats = useCallback(async (programOverride) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const prog = programOverride || activeReportProgram || hobbiesProgram;
      const res = await axios.get(`${API}/hobbies/stats`, { headers, params: { program: prog } });
      setHobbiesStats(res.data);
    } catch { /* non-critical */ }
  }, [token, activeReportProgram, hobbiesProgram]);

  const handleCamperSearch = (e) => {
    const q = e.target.value;
    setCamperSearch(q);
  };

  const handleDownloadPDF = async (camper) => {
    try {
      const res = await axios.get(`${API}/campers/${camper.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch (err) {
      toast.error("Failed to download PDF");
    }
  };

  const handleDownloadHobbiesPDF = async (scope, week) => {
    try {
      const res = await axios.get(`${API}/admin/hobbies-report.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { scope, week },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `hobbies_${scope}_week${week}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Failed to download PDF");
    }
  };

  const filteredCampers = campers.filter(c => {
    if (groupFilter && c.group_code !== groupFilter) return false;
    if (camperSearch) {
      const name = `${c.first_name || ""} ${c.last_name || ""}`.toLowerCase();
      return name.includes(camperSearch.toLowerCase());
    }
    return true;
  }).sort((a, b) =>
    (a.last_name || "").localeCompare(b.last_name || "") ||
    (a.first_name || "").localeCompare(b.first_name || "")
  );

  const menuCards = (division === "specialty") ? [
    { key: "specialist-reports", icon: AlertTriangle, label: "Specialist Incident Reports", color: "rose", badge: specialistUnread || null },
  ] : [
    { key: "campers", icon: Users, label: "Campers", color: "primary", badge: campers.length },
    { key: "daily-roster", icon: CalendarDays, label: "Daily Roster", color: "cyan" },
    { key: "attendance-sync", icon: ClipboardCheck, label: "Conflict Match", color: "rose", badge: attendanceConflicts.length || null },
    { key: "buddy-sheets", icon: ClipboardList, label: "Buddy Sheets", color: "emerald", badge: buddySheets.length || null },
    { key: "incidents", icon: AlertTriangle, label: "Incident Reports", color: "rose", badge: incidentReports.length || null },
    { key: "trip-forms", icon: Bus, label: "Trip Forms", color: "teal", badge: tripForms.length || null },
    { key: "messages", icon: Mail, label: "Messages", color: "blue" },
    { key: "schedules", icon: CalendarDays, label: "Schedules", color: "sky" },
    { key: "logins", icon: LogIn, label: "Logins", color: "indigo" },
    { key: "group-logins", icon: KeyRound, label: "Group Logins", color: "rose" },
    { key: "color-war", icon: Flag, label: "Color War", color: "fuchsia" },
  ];

  const externalLinks = [
    { key: "attendance", icon: CheckCircle, label: "Attendance", color: "violet", href: "http://rrdcattendance.com" },
    { key: "dining", icon: Utensils, label: "Dining", color: "yellow", href: "https://rrdcdining.com/admin" },
    { key: "staff-snapshot", icon: Shirt, label: "Staff Snapshot", color: "emerald", href: "https://rrdcstaffsnapshot.com" },
  ];

  // Forced password reset on first supervisor login (must_set_password JWT claim)
  const [mustSetPw, setMustSetPw] = useState(() => {
    const tk = localStorage.getItem("token");
    const p = decodeJwtPayload(tk);
    return !!p?.must_set_password && p?.user_type === "supervisor";
  });

  if (mustSetPw) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
        <SupervisorPasswordResetModal
          divisionName={divisionName}
          onComplete={() => setMustSetPw(false)}
          onLogout={logout}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100" data-testid="supervisor-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-sm border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {currentView !== "menu" && (
              <Button variant="ghost" size="sm" onClick={() => { setCurrentView("menu"); setGroupFilter(""); setCamperSearch(""); setCamperSubView("groups"); setRosterSubView("groups"); setRosterGroupFilter(""); setRosterGroupSearch(""); setRosterCamperSearch(""); setRosterData(null); setRosterSelectedDate(""); setHobbiesReport(null); setHobbiesHistory([]); setHobbiesStats(null); setMsgGroups([]); setMsgSelectedGroup(null); setMsgMessages([]); setMsgCamperFilter(""); setMsgDateFrom(""); setMsgDateTo(""); }} className="gap-1" data-testid="back-to-menu">
                <ArrowLeft className="w-4 h-4" />
                <span className="text-xs font-medium">Dashboard</span>
              </Button>
            )}
            <img src={LOGO_URL} alt="RRDC" className="h-10 w-auto object-contain" />
            <div className="hidden sm:block">
              <h1 className="font-heading font-bold text-sm text-primary-700">Camper Snapshot</h1>
              <p className="text-xs text-indigo-600 font-semibold">{divisionName} Supervisor</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isCurrentYear && (
              <Badge variant="outline" className="text-xs border-amber-300 text-amber-700 bg-amber-50" data-testid="sv-read-only-badge">
                Read Only
              </Badge>
            )}
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="h-8 px-2 text-xs border rounded-md bg-white font-semibold text-primary-700 cursor-pointer"
              data-testid="sv-year-selector"
            >
              {availableYears.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <Button variant="outline" size="sm" onClick={logout} data-testid="supervisor-logout">
              <LogOut className="w-4 h-4 mr-1" />Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-4 space-y-4">

        {/* ═══ MENU VIEW ═══ */}
        {currentView === "menu" && (
          <div className="space-y-4">
            <ScheduleNotificationsBanner groupCodes={Array.from(new Set((campers || []).map(c => c.group_code).filter(Boolean)))} />
            <MissingLoginAlert onClickGroup={() => setCurrentView("logins")} />
            {!isCurrentYear && (
              <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg" data-testid="sv-read-only-banner">
                <Calendar className="w-4 h-4 text-amber-600 flex-shrink-0" />
                <p className="text-xs font-semibold text-amber-800">Viewing {selectedYear} (Read Only)</p>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
                <CardContent className="p-4 flex items-center gap-3">
                  <Users className="w-8 h-8 text-indigo-600" />
                  <div>
                    <p className="font-heading font-bold text-xl text-indigo-800">{campers.length}</p>
                    <p className="text-xs text-indigo-600">Campers in {divisionName}</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200">
                <CardContent className="p-4 flex items-center gap-3">
                  <ClipboardList className="w-8 h-8 text-emerald-600" />
                  <div>
                    <p className="font-heading font-bold text-xl text-emerald-800">{groups.length}</p>
                    <p className="text-xs text-emerald-600">Groups</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Daily Camper Present Totals — Navigator (8 groups B12–B15, G12–G15) & Mariner (22 groups B01–B11, G01–G11) divisions */}
            {isNavigators && isCurrentYear && (
              <DailyAttendanceTotalsCard token={token} division="navigators" divisionLabel="Navigators (B12–B15, G12–G15)" />
            )}
            {isMariners && isCurrentYear && (
              <DailyAttendanceTotalsCard token={token} division="mariners" divisionLabel="Mariners (B01–B11, G01–G11)" />
            )}

            {/* Daily Deadlines Banner */}
            <div className="bg-red-50 border-2 border-red-300 rounded-2xl p-3" data-testid="sv-daily-deadlines-banner">
              <p className="text-xs font-black text-red-900 mb-2">DAILY DEADLINES</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                <div className="bg-white rounded-lg p-1.5 text-center border border-red-200">
                  <p className="text-sm font-bold text-red-700">9:20 AM</p>
                  <p className="text-[9px] text-red-600">Buddy Sheets</p>
                </div>
                <div className="bg-white rounded-lg p-1.5 text-center border border-red-200">
                  <p className="text-sm font-bold text-red-700">9:30 AM</p>
                  <p className="text-[9px] text-red-600">Bus Attendance</p>
                </div>
                <div className="bg-white rounded-lg p-1.5 text-center border border-red-200">
                  <p className="text-sm font-bold text-red-700">9:30 AM</p>
                  <p className="text-[9px] text-red-600">Lunch Orders</p>
                </div>
                <div className="bg-white rounded-lg p-1.5 text-center border border-red-200">
                  <p className="text-sm font-bold text-red-700">10:00 AM</p>
                  <p className="text-[9px] text-red-600">Group Attendance</p>
                </div>
              </div>
              {(isMariners || isNavigators) && (
                <div className="mt-2 pt-2 border-t border-red-200">
                  <p className="text-[9px] font-bold text-red-800 mb-1">WEEKLY: HOBBIES {isMariners ? "(G08-G11, B08-B11)" : "(B12-B15, G12-G15)"}</p>
                  <p className="text-[9px] text-red-700">Opens <strong>{isNavigators ? "Monday 9:00 AM" : "Monday 12:00 AM"}</strong> — Closes <strong>{isNavigators ? "Thursday 2:00 PM" : "Tuesday 2:00 PM"}</strong></p>
                  {isNavigators && (
                    <>
                      <p className="text-[9px] font-bold text-red-800 mt-1 mb-0.5">WEEKLY: INTENSIVES (B12-B15, G12-G15)</p>
                      <p className="text-[9px] text-red-700">Opens <strong>Monday 9:00 AM</strong> — Closes <strong>Tuesday 2:00 PM</strong></p>
                    </>
                  )}
                  {isMariners && (
                    <>
                      <p className="text-[9px] font-bold text-red-800 mt-1 mb-0.5">INTRAMURALS: Girls (G08-G11) &amp; Boys (B08-B11)</p>
                    </>
                  )}
                  {isNavigators && (
                    <>
                      <p className="text-[9px] font-bold text-red-800 mt-1 mb-0.5">INTRAMURALS: Navigator co-ed (B12-B15, G12-G15)</p>
                    </>
                  )}
                </div>
              )}
            </div>

            <MenuSearchBar containerSelector="[data-menu-grid='supervisor']" placeholder="Search supervisor tools…" testId="supervisor-menu-search" className="max-w-md" />
            <ReorderableGrid canReorder={["mariners", "skippers", "navigators"].includes(division)} menuGridId="supervisor" gridClassName="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {menuCards.map(card => (
                <button
                  key={card.key}
                  data-menu-card-label={card.label}
                  onClick={() => {
                    setCurrentView(card.key);
                    if (card.key === "daily-roster") { setRosterSubView("groups"); setRosterGroupFilter(""); setRosterGroupSearch(""); setRosterCamperSearch(""); setRosterData(null); setRosterSelectedDate(""); }
                    if (card.key === "specialist-reports") {
                      setSpecialistReportsLoading(true);
                      const h = { Authorization: `Bearer ${token}` };
                      Promise.all([
                        axios.get(`${API}/specialist/incident-reports?days=60`, { headers: h }),
                        axios.post(`${API}/specialist/incident-reports/mark-read`, {}, { headers: h }),
                      ]).then(([r]) => {
                        setSpecialistReports(r.data?.reports || []);
                        setSpecialistUnread(0);
                      }).catch(() => toast.error("Failed to load specialist reports"))
                        .finally(() => setSpecialistReportsLoading(false));
                    }
                    if (card.key === "messages") {
                      setMsgLoading(true);
                      const h = { Authorization: `Bearer ${token}` };
                      axios.get(`${API}/supervisor/messages/groups`, { headers: h }).then(res => {
                        setMsgGroups(res.data);
                      }).catch(() => toast.error("Failed to load message groups")).finally(() => setMsgLoading(false));
                    }
                  }}
                  className={`relative flex flex-col items-center gap-2 p-5 rounded-2xl border-2 hover:shadow-lg transition-all active:scale-95 bg-${card.color}-50 border-${card.color}-200 hover:border-${card.color}-400`}
                  data-testid={`sv-card-${card.key}`}
                >
                  {card.badge && (
                    <span className="absolute top-2 right-2 bg-primary text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">{card.badge}</span>
                  )}
                  <div className="w-10 h-10 flex items-center justify-center"><card.icon className={`w-5 h-5 text-${card.color}-600`} /></div>
                  <span className={`text-xs font-bold text-${card.color}-800`}>{card.label}</span>
                </button>
              ))}
              {externalLinks.map(link => (
                <a
                  key={link.key}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-menu-card-label={link.label}
                  className={`relative flex flex-col items-center gap-2 p-5 rounded-2xl border-2 hover:shadow-lg transition-all active:scale-95 bg-${link.color}-50 border-${link.color}-200 hover:border-${link.color}-400`}
                  data-testid={`sv-card-${link.key}`}
                >
                  <div className="w-10 h-10 flex items-center justify-center"><link.icon className={`w-5 h-5 text-${link.color}-600`} /></div>
                  <span className={`text-xs font-bold text-${link.color}-800`}>{link.label}</span>
                  <ExternalLink className="absolute top-2 right-2 w-3.5 h-3.5 text-muted-foreground" />
                </a>
              ))}
              {(isMariners || isNavigators) && isCurrentYear && (
                <button
                  data-menu-card-label="Hobbies Reports"
                  onClick={() => { setActiveReportProgram(hobbiesProgram); setCurrentView("hobbies-reports"); setHobbiesSubView("weekly"); setHobbiesWeek(isNavigators ? 2 : 1); fetchHobbiesReport(isNavigators ? 2 : 1, hobbiesProgram); fetchHobbiesStats(hobbiesProgram); }}
                  className="relative flex flex-col items-center gap-2 p-5 rounded-2xl border-2 hover:shadow-lg transition-all active:scale-95 bg-fuchsia-50 border-fuchsia-200 hover:border-fuchsia-400"
                  data-testid="sv-card-hobbies-reports"
                >
                  <div className="w-10 h-10 flex items-center justify-center"><Sparkles className="w-5 h-5 text-fuchsia-600" /></div>
                  <span className="text-xs font-bold text-fuchsia-800">Hobbies Reports</span>
                </button>
              )}
              {isNavigators && isCurrentYear && (
                <button
                  onClick={() => { setActiveReportProgram("intensive"); setCurrentView("hobbies-reports"); setHobbiesSubView("weekly"); setHobbiesWeek(1); fetchHobbiesReport(1, "intensive"); fetchHobbiesStats("intensive"); }}
                  className="relative flex flex-col items-center gap-2 p-5 rounded-2xl border-2 hover:shadow-lg transition-all active:scale-95 bg-amber-50 border-amber-200 hover:border-amber-400"
                  data-testid="sv-card-intensives-reports"
                >
                  <div className="w-10 h-10 flex items-center justify-center"><Sparkles className="w-5 h-5 text-amber-600" /></div>
                  <span className="text-xs font-bold text-amber-800">Intensives Reports</span>
                </button>
              )}
              {isNavigators && isCurrentYear && (
                <button
                  onClick={() => setCurrentView("nav-intramurals")}
                  className="relative flex flex-col items-center gap-2 p-5 rounded-2xl border-2 hover:shadow-lg transition-all active:scale-95 bg-teal-50 border-teal-200 hover:border-teal-400"
                  data-testid="sv-card-nav-intramurals"
                >
                  <div className="w-10 h-10 flex items-center justify-center"><Trophy className="w-5 h-5 text-teal-600" /></div>
                  <span className="text-xs font-bold text-teal-800">Nav Intramurals</span>
                </button>
              )}
              {isNavigators && isCurrentYear && (
                <button
                  onClick={() => {
                    setTripRostersLoading(true);
                    const t = localStorage.getItem("token");
                    axios.get(`${API}/cit/overnight`, { headers: { Authorization: `Bearer ${t}` } })
                      .then(res => setTripRostersData(res.data))
                      .catch(() => toast.error("Failed to load trip rosters"))
                      .finally(() => setTripRostersLoading(false));
                    setCurrentView("trip-rosters");
                  }}
                  className="relative flex flex-col items-center gap-2 p-5 rounded-2xl border-2 hover:shadow-lg transition-all active:scale-95 bg-slate-50 border-slate-300 hover:border-slate-500"
                  data-testid="sv-card-trip-rosters"
                >
                  <div className="w-10 h-10 flex items-center justify-center"><Moon className="w-5 h-5 text-slate-700" /></div>
                  <span className="text-xs font-bold text-slate-800">Overnight & Broadway</span>
                </button>
              )}
              {isMariners && isCurrentYear && (
                <button
                  onClick={() => setCurrentView("mariner-intramurals")}
                  className="relative flex flex-col items-center gap-2 p-5 rounded-2xl border-2 hover:shadow-lg transition-all active:scale-95 bg-blue-50 border-blue-200 hover:border-blue-400"
                  data-testid="sv-card-mariner-intramurals"
                >
                  <div className="w-10 h-10 flex items-center justify-center"><Trophy className="w-5 h-5 text-blue-600" /></div>
                  <span className="text-xs font-bold text-blue-800">Mariner Intramurals</span>
                </button>
              )}
            </ReorderableGrid>
          </div>
        )}

        {/* ═══ CAMPERS VIEW - Group Cards ═══ */}
        {currentView === "campers" && camperSubView === "groups" && (
          <div className="space-y-4">
            <div>
              <h2 className="font-heading font-bold text-lg">{divisionName} Groups</h2>
              <p className="text-sm text-muted-foreground">{campers.length} campers across {groups.length} groups</p>
            </div>
            <div className="relative" data-testid="sv-camper-global-search">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input 
                placeholder="Search any camper by name..." 
                value={camperSearch} 
                onChange={(e) => setCamperSearch(e.target.value)} 
                className="pl-10" 
                data-testid="sv-global-camper-search"
              />
            </div>
            {camperSearch.trim() ? (
              <div className="space-y-2">
                {campers
                  .filter(c => {
                    const q = camperSearch.toLowerCase();
                    const full = `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase();
                    return full.includes(q);
                  })
                  .sort((a, b) => `${a.last_name}`.localeCompare(`${b.last_name}`))
                  .slice(0, 30)
                  .map(c => (
                    <button
                      key={c.id}
                      onClick={() => { setSelectedCamper(c); }}
                      className="w-full flex items-center justify-between p-3 rounded-lg border bg-white hover:bg-indigo-50 hover:border-indigo-300 transition-all text-left"
                      data-testid={`sv-search-result-${c.id}`}
                    >
                      <div>
                        <span className="text-sm font-semibold text-indigo-800">{c.first_name} {c.last_name}</span>
                      </div>
                      <Badge variant="secondary" className="text-xs bg-indigo-50 text-indigo-700 border-indigo-200">{c.group_code}</Badge>
                    </button>
                  ))
                }
                {campers.filter(c => {
                  const q = camperSearch.toLowerCase();
                  return `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase().includes(q);
                }).length === 0 && (
                  <p className="text-center text-sm text-muted-foreground py-8">No campers match "{camperSearch}"</p>
                )}
              </div>
            ) : (
            <>
            {groups.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>No groups found for {divisionName}</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {groups.sort((a, b) => (a.code || "").localeCompare(b.code || "", undefined, { numeric: true })).map(group => {
                  const groupCamperCount = campers.filter(c => c.group_code === group.code).length;
                  return (
                    <button
                      key={group.code}
                      onClick={() => { setGroupFilter(group.code); setCamperSubView("list"); }}
                      className="flex flex-col items-center gap-2 p-5 rounded-2xl border-2 bg-white border-indigo-200 hover:border-indigo-400 hover:shadow-lg transition-all active:scale-95"
                      data-testid={`sv-group-card-${group.code}`}
                    >
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-100 to-indigo-200 flex items-center justify-center">
                        <Users className="w-6 h-6 text-indigo-600" />
                      </div>
                      <span className="text-sm font-bold text-indigo-800">{group.code}</span>
                      {group.name && <span className="text-xs text-muted-foreground">{group.name}</span>}
                      <Badge variant="secondary" className="text-xs bg-indigo-50 text-indigo-700 border-indigo-200">
                        {groupCamperCount} camper{groupCamperCount !== 1 ? "s" : ""}
                      </Badge>
                    </button>
                  );
                })}
              </div>
            )}
            </>
            )}
          </div>
        )}

        {/* ═══ CAMPERS VIEW - Camper List ═══ */}
        {currentView === "campers" && camperSubView === "list" && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Button variant="ghost" size="sm" onClick={() => { setCamperSubView("groups"); setGroupFilter(""); setCamperSearch(""); }} className="px-2" data-testid="sv-back-to-groups">
                  <ArrowLeft className="w-4 h-4" />
                </Button>
                <div>
                  <h2 className="font-heading font-bold text-lg flex items-center gap-2">
                    <Badge variant="secondary" className="text-sm bg-indigo-50 text-indigo-700 border-indigo-200 px-3 py-1">{groupFilter}</Badge>
                    {groups.find(g => g.code === groupFilter)?.name || ""}
                  </h2>
                  <p className="text-sm text-muted-foreground">{filteredCampers.length} campers</p>
                </div>
              </div>
              <div className="relative w-full sm:w-56">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input placeholder="Search by name..." value={camperSearch} onChange={handleCamperSearch} className="pl-10" data-testid="sv-camper-search" />
              </div>
            </div>

            {filteredCampers.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>{camperSearch ? "No campers match your search" : "No campers in this group"}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {filteredCampers.map(camper => (
                  <SvCamperCard key={camper.id} camper={camper} onClick={() => setSelectedCamper(camper)} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Camper Detail Modal */}
        {selectedCamper && (
          <Dialog open={!!selectedCamper} onOpenChange={() => setSelectedCamper(null)}>
            <DialogContent className="max-w-2xl max-h-[90vh] p-0 w-[calc(100%-2rem)] sm:w-full rounded-xl" aria-describedby="sv-camper-desc">
              <span id="sv-camper-desc" className="sr-only">Camper details</span>
              <SvCamperDetail camper={selectedCamper} onClose={() => setSelectedCamper(null)} onDownloadPDF={handleDownloadPDF} readOnly={!isCurrentYear} />
            </DialogContent>
          </Dialog>
        )}

        {/* ═══ DAILY ROSTER — GROUP CARDS ═══ */}
        {currentView === "daily-roster" && rosterSubView === "groups" && (
          <div className="space-y-4">
            <div>
              <h2 className="font-heading font-bold text-lg">Daily Roster — {divisionName}</h2>
              <p className="text-sm text-muted-foreground">Select a group to view today's roster</p>
            </div>
            {groups.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>No groups found</p>
              </div>
            ) : (
              <>
                <div className="relative max-w-md">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search group code or name (e.g. B05, Lobsters)..."
                    value={rosterGroupSearch}
                    onChange={(e) => setRosterGroupSearch(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400"
                    data-testid="sv-roster-group-search"
                  />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                  {groups
                    .filter(g => {
                      const q = rosterGroupSearch.trim().toLowerCase();
                      if (!q) return true;
                      return (g.code || "").toLowerCase().includes(q) || (g.name || "").toLowerCase().includes(q);
                    })
                    .sort((a, b) => (a.code || "").localeCompare(b.code || "", undefined, { numeric: true }))
                    .map(group => (
                      <button
                        key={group.code}
                        onClick={() => { setRosterGroupFilter(group.code); setRosterSubView("list"); setRosterCamperSearch(""); fetchRosterForGroup(group.code, rosterSelectedDate || undefined); }}
                        className="flex flex-col items-center gap-2 p-5 rounded-2xl border-2 bg-white border-cyan-200 hover:border-cyan-400 hover:shadow-lg transition-all active:scale-95"
                        data-testid={`sv-roster-group-${group.code}`}
                      >
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-100 to-cyan-200 flex items-center justify-center">
                          <CalendarDays className="w-6 h-6 text-cyan-600" />
                        </div>
                        <span className="text-sm font-bold text-cyan-800">{group.code}</span>
                        {group.name && <span className="text-xs text-muted-foreground">{group.name}</span>}
                      </button>
                    ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* ═══ DAILY ROSTER — GROUP ROSTER LIST ═══ */}
        {currentView === "daily-roster" && rosterSubView === "list" && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-3">
                <Button variant="ghost" size="sm" onClick={() => { setRosterSubView("groups"); setRosterGroupFilter(""); setRosterCamperSearch(""); setRosterData(null); }} className="px-2" data-testid="sv-roster-back">
                  <ArrowLeft className="w-4 h-4" />
                </Button>
                <div>
                  <h2 className="font-heading font-bold text-lg flex items-center gap-2">
                    <Badge variant="secondary" className="text-sm bg-cyan-50 text-cyan-700 border-cyan-200 px-3 py-1">{rosterGroupFilter}</Badge>
                    {groups.find(g => g.code === rosterGroupFilter)?.name || ""}
                  </h2>
                  {rosterData && (() => {
                    const presentCount = (rosterData.campers || []).filter(c => (c.attendance || "").toLowerCase() !== "absent").length;
                    const absentCount = (rosterData.campers || []).filter(c => (c.attendance || "").toLowerCase() === "absent").length;
                    return (
                      <p className="text-sm text-muted-foreground">
                        Roster for {new Date(rosterData.date + "T12:00:00").toLocaleDateString("en-US", { timeZone: "America/New_York", weekday: "long", month: "long", day: "numeric", year: "numeric" })} — {presentCount} camper{presentCount !== 1 ? "s" : ""}
                        {absentCount > 0 ? ` (${absentCount} absent — hidden)` : ""}
                      </p>
                    );
                  })()}
                </div>
              </div>
              <div className="flex items-center gap-2 ml-10 sm:ml-0">
                <label className="text-xs font-medium text-cyan-700">View date:</label>
                <input
                  type="date"
                  min="2026-06-29"
                  max="2026-08-20"
                  value={rosterSelectedDate || rosterData?.date || ""}
                  onChange={(e) => { setRosterSelectedDate(e.target.value); fetchRosterForGroup(rosterGroupFilter, e.target.value); }}
                  className="px-2 py-1 text-sm border border-cyan-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-cyan-400"
                  data-testid="sv-roster-date-picker"
                />
              </div>
            </div>
            {rosterLoading ? (
              <div className="text-center py-16">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading roster...</p>
              </div>
            ) : !rosterData?.is_camp_day || (rosterData?.campers || []).length === 0 ? (
              <div className="text-center py-16">
                <CalendarDays className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                <h3 className="text-xl text-muted-foreground mb-2">No campers scheduled for this date.</h3>
                <p className="text-sm text-muted-foreground">
                  {!rosterData?.is_camp_day ? "This is not a camp operating day." : "No campers match the session filter for this date."}
                </p>
              </div>
            ) : (
              <>
                <div className="relative max-w-md">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search camper by name..."
                    value={rosterCamperSearch}
                    onChange={(e) => setRosterCamperSearch(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400"
                    data-testid="sv-roster-camper-search"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
                  {[...(rosterData?.campers || [])]
                    .filter(c => (c.attendance || "").toLowerCase() !== "absent")
                    .filter(c => {
                      const q = rosterCamperSearch.trim().toLowerCase();
                      if (!q) return true;
                      const full = `${c.first_name || ""} ${c.last_name || ""}`.toLowerCase();
                      const pref = `${c.preferred_name || ""} ${c.last_name || ""}`.toLowerCase();
                      return full.includes(q) || pref.includes(q) || (c.last_name || "").toLowerCase().includes(q);
                    })
                    .sort((a, b) =>
                      (a.last_name || "").localeCompare(b.last_name || "", undefined, { sensitivity: "base" }) ||
                      (a.first_name || "").localeCompare(b.first_name || "", undefined, { sensitivity: "base" })
                    ).map(camper => {
                  const displayName = camper.first_name;
                  const initials = `${camper.first_name?.[0] || ''}${camper.last_name?.[0] || ''}`.toUpperCase();
                  const sessionTags = (camper.sessions || []).map(s => SESSION_SHORT_LABELS[s.name] || s.name).filter(Boolean);
                  return (
                    <div
                      key={camper.id}
                      onClick={() => setSelectedCamper(camper)}
                      className="group relative overflow-hidden rounded-xl border bg-white shadow-sm hover:shadow-lg transition-all duration-200 hover:-translate-y-1 cursor-pointer active:scale-[0.98]"
                      data-testid={`sv-roster-camper-${camper.id}`}
                    >
                      <div className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-cyan-100 to-cyan-200 flex items-center justify-center flex-shrink-0">
                            <span className="font-bold text-cyan-600 text-sm">{initials}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-sm truncate">{displayName} {camper.last_name}</h3>
                            <div className="flex flex-wrap items-center gap-1.5 mt-1">
                              {camper.group_code && <Badge variant="secondary" className="text-xs">{camper.group_code}</Badge>}
                              {camper.age && <span className="text-xs text-muted-foreground">Age {Math.floor(camper.age)}</span>}
                              {sessionTags.map(tag => (
                                <Badge key={tag} variant="outline" className={`text-xs ${tag.startsWith('F') && tag !== 'FS5' ? 'border-orange-300 text-orange-600 bg-orange-50' : 'border-emerald-300 text-emerald-700 bg-emerald-50'}`}>
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="px-4 py-2 bg-muted/30 border-t text-xs text-muted-foreground flex items-center justify-between">
                        <span>View Details</span>
                        <span className="opacity-0 group-hover:opacity-100 transition-opacity">&rarr;</span>
                      </div>
                    </div>
                  );
                })}
                </div>
              </>
            )}
          </div>
        )}

        {/* ═══ ATTENDANCE SYNC VIEW ═══ */}
        {currentView === "attendance-sync" && (
          <div className="space-y-4" data-testid="sv-attendance-sync">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <h2 className="font-heading font-bold text-lg flex items-center gap-2">
                  <ClipboardCheck className="w-5 h-5 text-rose-600" />
                  Conflict Match — {divisionName}
                </h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {attendanceConflictsDate ? `Conflicts for ${new Date(attendanceConflictsDate + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}` : "Today's attendance conflicts"} · Resolve before 9:35 AM
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  try {
                    const res = await axios.get(`${API}/attendance/conflicts`, { headers: { Authorization: `Bearer ${token}` } });
                    setAttendanceConflicts((res.data?.conflicts || []).filter(c => pattern.test(c.group_code || "")));
                    setAttendanceConflictsDate(res.data?.date || "");
                    toast.success("Refreshed");
                  } catch (e) {
                    toast.error("Failed to refresh");
                  }
                }}
                className="text-xs"
                data-testid="sv-conflicts-refresh"
              >
                <RefreshCw className="w-3.5 h-3.5 mr-1" />Refresh
              </Button>
            </div>

            {attendanceConflicts.length === 0 ? (
              <div className="text-center py-16 bg-emerald-50 rounded-xl border border-emerald-200">
                <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-3" />
                <p className="text-base font-medium text-emerald-800">No attendance conflicts</p>
                <p className="text-sm text-emerald-700 mt-1">Front desk and bus marks agree across all your campers.</p>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-sm text-rose-900">
                  <strong>{attendanceConflicts.length}</strong> conflict{attendanceConflicts.length !== 1 ? "s" : ""} need resolution. Tap <strong>Accept App</strong> or <strong>Accept Bus</strong> to lock in the correct mark for the day.
                </div>
                {attendanceConflicts.map(c => (
                  <div key={c.camper_id} className="bg-white border-2 border-rose-300 rounded-lg p-3 shadow-sm" data-testid={`conflict-${c.camper_id}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm">{c.camper_name}</div>
                        <div className="text-[11px] text-muted-foreground">{c.group_code}</div>
                        <div className="flex flex-wrap gap-2 mt-2">
                          <Badge variant="outline" className="text-[10px] bg-blue-50 border-blue-300 text-blue-800">
                            App says: <strong className="ml-1">{c.app_status}</strong>
                          </Badge>
                          <Badge variant="outline" className="text-[10px] bg-amber-50 border-amber-300 text-amber-800">
                            Bus says: <strong className="ml-1">{c.bus_status}</strong>
                          </Badge>
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2 shrink-0">
                        <Button
                          size="sm"
                          disabled={resolvingConflict === c.camper_id}
                          onClick={async () => {
                            setResolvingConflict(c.camper_id);
                            try {
                              await axios.post(`${API}/attendance/resolve-conflict`,
                                { camper_id: c.camper_id, date: attendanceConflictsDate, accept: "app" },
                                { headers: { Authorization: `Bearer ${token}` } }
                              );
                              setAttendanceConflicts(prev => prev.filter(x => x.camper_id !== c.camper_id));
                              toast.success(`Accepted "${c.app_status}" for ${c.camper_name}`);
                            } catch (e) {
                              toast.error(e.response?.data?.detail || "Failed");
                            } finally {
                              setResolvingConflict(null);
                            }
                          }}
                          className="bg-blue-600 hover:bg-blue-700 text-xs"
                          data-testid={`accept-app-${c.camper_id}`}
                        >
                          Accept App
                        </Button>
                        <Button
                          size="sm"
                          disabled={resolvingConflict === c.camper_id}
                          onClick={async () => {
                            setResolvingConflict(c.camper_id);
                            try {
                              await axios.post(`${API}/attendance/resolve-conflict`,
                                { camper_id: c.camper_id, date: attendanceConflictsDate, accept: "bus" },
                                { headers: { Authorization: `Bearer ${token}` } }
                              );
                              setAttendanceConflicts(prev => prev.filter(x => x.camper_id !== c.camper_id));
                              toast.success(`Accepted "${c.bus_status}" for ${c.camper_name}`);
                            } catch (e) {
                              toast.error(e.response?.data?.detail || "Failed");
                            } finally {
                              setResolvingConflict(null);
                            }
                          }}
                          className="bg-amber-600 hover:bg-amber-700 text-xs"
                          data-testid={`accept-bus-${c.camper_id}`}
                        >
                          Accept Bus
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ═══ BUDDY SHEETS VIEW ═══ */}
        {currentView === "buddy-sheets" && (
          <div className="space-y-4">
            <h2 className="font-heading font-bold text-lg">{divisionName} Buddy Sheets</h2>
            {buddySheets.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <ClipboardList className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>No buddy sheets submitted for {divisionName} groups</p>
              </div>
            ) : (
              <div className="space-y-3">
                {Object.entries(buddySheets.reduce((groups, sheet) => {
                  const d = sheet.date || "Unknown";
                  (groups[d] = groups[d] || []).push(sheet);
                  return groups;
                }, {})).sort(([a], [b]) => b.localeCompare(a)).map(([date, sheets], dayIdx) => (
                  <details key={date} open={dayIdx === 0} className="border rounded-xl overflow-hidden bg-white shadow-sm" data-testid={`sv-bs-day-${date}`}>
                    <summary className="px-4 py-3 bg-amber-50 cursor-pointer hover:bg-amber-100 transition-colors font-semibold text-sm flex items-center justify-between">
                      <span>{new Date(date + "T12:00:00").toLocaleDateString("en-US", { timeZone: "America/New_York", weekday: "long", month: "short", day: "numeric", year: "numeric" })}</span>
                      <Badge variant="secondary" className="text-xs">{sheets.length} sheet{sheets.length !== 1 ? "s" : ""}</Badge>
                    </summary>
                    <div className="p-3 space-y-2 border-t">
                      {sheets.map(sheet => (
                        <div
                          key={sheet.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => setViewingSheet(sheet)}
                          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setViewingSheet(sheet); } }}
                          className="w-full border rounded-lg p-3 bg-amber-50/30 hover:bg-amber-100/60 transition-colors flex items-center justify-between gap-3 cursor-pointer"
                          data-testid={`sv-bs-${sheet.id}`}
                        >
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge variant="secondary">{sheet.group_code}</Badge>
                              <Badge className={sheet.period === "AM" ? "bg-amber-600 text-white text-[10px]" : "bg-indigo-600 text-white text-[10px]"}>{sheet.period}</Badge>
                              <Badge className={sheet.status === "locked" ? "bg-red-100 text-red-800 text-[10px]" : "bg-green-100 text-green-800 text-[10px]"}>{sheet.status}</Badge>
                            </div>
                            <p className="text-[11px] text-muted-foreground mt-1">
                              {(sheet.rows || []).filter(r => r.buddy_a || r.buddy_b || r.buddy_c).length} rows filled · click to view
                            </p>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className="inline-flex items-center text-xs gap-1 px-2 py-1 rounded-md border bg-white text-amber-800 hover:bg-amber-50">
                              <Eye className="w-3.5 h-3.5" />View
                            </span>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  const res = await axios.get(`${API}/buddy-sheets/${sheet.id}/pdf`, { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" });
                                  window.open(window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" })), "_blank");
                                } catch { toast.error("Failed to load PDF"); }
                              }}
                              data-testid={`sv-bs-pdf-${sheet.id}`}
                            >
                              <Download className="w-3.5 h-3.5 mr-1" />PDF
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </details>
                ))}
              </div>
            )}

            {/* View Buddy Sheet Modal */}
            {viewingSheet && (
              <Dialog open={!!viewingSheet} onOpenChange={() => setViewingSheet(null)}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="sv-bs-view-dialog">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <Droplets className="w-5 h-5 text-amber-600" />
                      {viewingSheet.group_name || viewingSheet.group_code} — {viewingSheet.period}
                    </DialogTitle>
                    <DialogDescription>
                      {new Date(viewingSheet.date + "T12:00:00").toLocaleDateString('en-US', { timeZone: 'America/New_York', weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })} · Status: {viewingSheet.status}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-3 mt-2">
                    {(() => {
                      const rows = viewingSheet.rows || [];
                      const sections = [
                        { key: "red", label: "Red — Deep End (Rows 1–10)", bg: "bg-red-50", header: "bg-red-500 text-white", border: "border-red-300" },
                        { key: "yellow", label: "Yellow — Middle (Rows 11–20)", bg: "bg-yellow-50", header: "bg-yellow-400 text-yellow-900", border: "border-yellow-300" },
                        { key: "green", label: "Green — Shallow (Rows 21–30)", bg: "bg-green-50", header: "bg-green-500 text-white", border: "border-green-300" },
                      ];
                      const grouped = { red: [], yellow: [], green: [] };
                      for (const r of rows) {
                        const sec = (r.section || "green").toLowerCase();
                        (grouped[sec] || grouped.green).push(r);
                      }
                      return sections.map(sec => (
                        grouped[sec.key].length > 0 && (
                          <div key={sec.key} className={`rounded-lg overflow-hidden border-2 ${sec.border}`} data-testid={`sv-bs-section-${sec.key}`}>
                            <div className={`${sec.header} px-3 py-2 text-xs font-bold uppercase tracking-wide`}>{sec.label}</div>
                            <div className={`divide-y ${sec.bg}`}>
                              <div className="grid grid-cols-[40px_1fr_1fr_1fr] gap-2 px-3 py-1.5 text-[10px] font-bold uppercase text-slate-700">
                                <div>#</div><div>Buddy A</div><div>Buddy B</div><div>Buddy C</div>
                              </div>
                              {grouped[sec.key].map((row, idx) => {
                                const empty = !row.buddy_a && !row.buddy_b && !row.buddy_c;
                                return (
                                  <div key={idx} className={`grid grid-cols-[40px_1fr_1fr_1fr] gap-2 px-3 py-1.5 text-sm bg-white ${empty ? "opacity-40" : ""}`}>
                                    <div className="font-bold text-slate-600">{row.row_num}</div>
                                    <div>{row.buddy_a || "—"}</div>
                                    <div>{row.buddy_b || "—"}</div>
                                    <div>{row.buddy_c || "—"}</div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )
                      ));
                    })()}
                    {viewingSheet.comments && (
                      <div className="bg-muted/30 rounded-lg p-3 border">
                        <p className="text-xs font-medium text-muted-foreground mb-1">Comments</p>
                        <p className="text-sm whitespace-pre-wrap">{viewingSheet.comments}</p>
                      </div>
                    )}
                    <div className="flex justify-end gap-2 pt-2">
                      <Button variant="outline" size="sm" onClick={() => setViewingSheet(null)} data-testid="sv-bs-view-close">Close</Button>
                      <Button
                        size="sm"
                        onClick={async () => {
                          try {
                            const res = await axios.get(`${API}/buddy-sheets/${viewingSheet.id}/pdf`, { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" });
                            window.open(window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" })), "_blank");
                          } catch { toast.error("Failed to load PDF"); }
                        }}
                        data-testid="sv-bs-view-download"
                      >
                        <Download className="w-4 h-4 mr-1" />Download PDF
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        )}

        {/* ═══ INCIDENT REPORTS VIEW ═══ */}
        {currentView === "incidents" && (
          <div className="space-y-4">
            <h2 className="font-heading font-bold text-lg">{divisionName} Incident Reports</h2>
            <div className="flex flex-wrap items-center gap-2" data-testid="sv-incident-search-bar">
              <div className="relative flex-1 min-w-[180px]">
                <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search by camper name or group…"
                  value={incidentSearch}
                  onChange={(e) => setIncidentSearch(e.target.value)}
                  className="pl-8 h-9 bg-white"
                  data-testid="sv-incident-search-input"
                />
              </div>
              <input
                type="date"
                value={incidentDateFilter}
                onChange={(e) => setIncidentDateFilter(e.target.value)}
                className="h-9 border rounded-md px-2 text-sm bg-white"
                data-testid="sv-incident-date-filter"
              />
              {incidentFilterActive && (
                <Button variant="ghost" size="sm" className="h-9 text-xs" onClick={() => { setIncidentSearch(""); setIncidentDateFilter(""); }} data-testid="sv-incident-clear-filters">
                  Clear ({filteredIncidents.length})
                </Button>
              )}
            </div>
            {filteredIncidents.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <AlertTriangle className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>{incidentFilterActive ? "No reports match your search" : `No incident reports for ${divisionName} groups`}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {Object.entries(filteredIncidents.reduce((groups, report) => {
                  const d = report.date || "Unknown";
                  (groups[d] = groups[d] || []).push(report);
                  return groups;
                }, {})).sort(([a], [b]) => b.localeCompare(a)).map(([date, reports], dayIdx) => (
                  <details key={date} open={incidentFilterActive || dayIdx === 0} className="border rounded-xl overflow-hidden bg-white shadow-sm" data-testid={`sv-incident-day-${date}`}>
                    <summary className="px-4 py-3 bg-orange-50 cursor-pointer hover:bg-orange-100 transition-colors font-semibold text-sm flex items-center justify-between">
                      <span>{new Date(date + "T12:00:00").toLocaleDateString("en-US", { timeZone: "America/New_York", weekday: "long", month: "short", day: "numeric", year: "numeric" })}</span>
                      <Badge variant="secondary" className="text-xs">{reports.length} report{reports.length !== 1 ? "s" : ""}</Badge>
                    </summary>
                    <div className="p-3 space-y-3 border-t">
                      {reports.map(report => (
                        <Card key={report.id} data-testid={`sv-incident-${report.id}`}>
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <Badge variant="secondary">{report.group_code}</Badge>
                                  {report.great_day ? (
                                    <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Great Day</Badge>
                                  ) : (
                                    <Badge className={report.incident_type === "Medical" ? "bg-red-100 text-red-800" : "bg-orange-100 text-orange-800"}>{report.incident_type}</Badge>
                                  )}
                                </div>
                                {report.camper_names?.length > 0 && (
                                  <p className="text-sm mt-1"><span className="text-muted-foreground">Camper(s):</span> <span className="font-medium">{report.camper_names.join(", ")}</span></p>
                                )}
                                {report.description && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{report.description}</p>}
                                {report.ai_summary && (
                                  <div className="mt-2 p-2 bg-blue-50 border border-blue-100 rounded-lg">
                                    <p className="text-xs font-semibold text-blue-700 mb-0.5">AI Summary</p>
                                    <p className="text-xs text-blue-900">{report.ai_summary}</p>
                                  </div>
                                )}
                              </div>
                              <div className="flex flex-col gap-1.5 flex-shrink-0">
                                <Button variant="outline" size="sm" onClick={() => setEditingReport(report)} data-testid={`sv-incident-edit-${report.id}`}>
                                  <Edit className="w-3.5 h-3.5 mr-1" />Edit
                                </Button>
                                <Button variant="outline" size="sm" onClick={async () => {
                                  try {
                                    const res = await axios.get(`${API}/incident-reports/${report.id}/pdf`, { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" });
                                    window.open(window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" })), "_blank");
                                  } catch { toast.error("Failed to load PDF"); }
                                }} data-testid={`sv-incident-pdf-${report.id}`}>
                                  <Download className="w-3.5 h-3.5 mr-1" />PDF
                                </Button>
                              </div>
                            </div>
                            {!report.great_day && (
                              <SupervisorNotesEditor
                                report={report}
                                token={token}
                                onUpdate={(updated) => setIncidentReports(prev => prev.map(r => r.id === updated.id ? updated : r))}
                              />
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </details>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ═══ TRIP FORMS VIEW ═══ */}
        {currentView === "trip-forms" && (
          <div className="space-y-4">
            <h2 className="font-heading font-bold text-lg">{divisionName} Trip Forms</h2>
            {tripForms.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Bus className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>No trip forms for {divisionName} groups</p>
              </div>
            ) : (
              <div className="space-y-3">
                {Object.entries(tripForms.reduce((groups, form) => {
                  const d = form.date || "Unknown";
                  (groups[d] = groups[d] || []).push(form);
                  return groups;
                }, {})).sort(([a], [b]) => b.localeCompare(a)).map(([date, forms], dayIdx) => (
                  <details key={date} open={dayIdx === 0} className="border rounded-xl overflow-hidden bg-white shadow-sm" data-testid={`sv-tf-day-${date}`}>
                    <summary className="px-4 py-3 bg-green-50 cursor-pointer hover:bg-green-100 transition-colors font-semibold text-sm flex items-center justify-between">
                      <span>{new Date(date + "T12:00:00").toLocaleDateString("en-US", { timeZone: "America/New_York", weekday: "long", month: "short", day: "numeric", year: "numeric" })}</span>
                      <Badge variant="secondary" className="text-xs">{forms.length} form{forms.length !== 1 ? "s" : ""}</Badge>
                    </summary>
                    <div className="p-3 space-y-3 border-t">
                      {forms.map(form => (
                        <details key={form.id} className="border rounded-lg overflow-hidden" data-testid={`sv-tf-${form.id}`}>
                          <summary className="px-4 py-2.5 bg-muted/20 cursor-pointer hover:bg-muted/40">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Badge variant="secondary">{form.group_code}</Badge>
                                <span className="text-sm font-semibold">{form.trip_name || "Trip Form"}</span>
                              </div>
                              <Button variant="outline" size="sm" className="shrink-0" onClick={async (e) => {
                                e.preventDefault(); e.stopPropagation();
                                try {
                                  const res = await axios.get(`${API}/trip-forms/${form.id}/pdf`, { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" });
                                  window.open(window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" })), "_blank");
                                } catch { toast.error("Failed to load PDF"); }
                              }} data-testid={`sv-tf-pdf-${form.id}`}>
                                <Download className="w-3.5 h-3.5 mr-1" />PDF
                              </Button>
                            </div>
                          </summary>
                          <div className="px-4 pb-4 pt-2 space-y-3 border-t">
                            {(form.counselor_sections || []).map((sec, idx) => (
                              <div key={idx} className="border rounded-lg p-3 bg-slate-50">
                                <p className="text-sm font-semibold text-slate-800 mb-2">Counselor: {sec.counselor}</p>
                                <div className="flex flex-wrap gap-1.5">
                                  {(sec.campers || []).map((c, ci) => <Badge key={ci} variant="secondary" className="text-xs">{c}</Badge>)}
                                  {(!sec.campers || sec.campers.length === 0) && <span className="text-xs text-muted-foreground">No campers listed</span>}
                                </div>
                              </div>
                            ))}
                            <p className="text-xs text-muted-foreground">Submitted by: {form.submitted_by} at {new Date(form.submitted_at).toLocaleString("en-US", { timeZone: "America/New_York" })}</p>
                          </div>
                        </details>
                      ))}
                    </div>
                  </details>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ═══ HOBBIES REPORTS VIEW ═══ */}
        {currentView === "hobbies-reports" && (
          <HobbiesReportsView
            hobbiesSubView={hobbiesSubView}
            setHobbiesSubView={setHobbiesSubView}
            hobbiesWeek={hobbiesWeek}
            setHobbiesWeek={setHobbiesWeek}
            hobbiesReport={hobbiesReport}
            hobbiesHistory={hobbiesHistory}
            hobbiesLoading={hobbiesLoading}
            hobbiesHistoryFilter={hobbiesHistoryFilter}
            setHobbiesHistoryFilter={setHobbiesHistoryFilter}
            fetchHobbiesReport={fetchHobbiesReport}
            fetchHobbiesHistory={fetchHobbiesHistory}
            hobbiesStats={hobbiesStats}
            hobbiesProgram={activeReportProgram || hobbiesProgram}
            onDownloadHobbiesPDF={handleDownloadHobbiesPDF}
          />
        )}

        {/* ═══ MESSAGES VIEW ═══ */}
        {currentView === "messages" && !msgSelectedGroup && (
          <div className="space-y-4" data-testid="sv-messages-groups">
            <h2 className="font-heading font-bold text-lg text-blue-800 flex items-center gap-2">
              <Mail className="w-5 h-5" /> Parent Messages by Group
            </h2>
            <DivisionBroadcastBar token={token} divisionName={divisionName} />
            {msgLoading ? (
              <div className="text-center py-10 text-muted-foreground">Loading groups...</div>
            ) : msgGroups.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground">No messages found for your division</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-2">
                {msgGroups.map(g => (
                  <button
                    key={g.group_code}
                    onClick={() => {
                      setMsgSelectedGroup(g.group_code);
                      setMsgLoading(true);
                      setMsgCamperFilter("");
                      setMsgDateFrom("");
                      setMsgDateTo("");
                      const h = { Authorization: `Bearer ${token}` };
                      axios.get(`${API}/supervisor/messages/${g.group_code}`, { headers: h }).then(res => setMsgMessages(res.data)).catch(() => toast.error("Failed to load messages")).finally(() => setMsgLoading(false));
                    }}
                    className="flex flex-col items-center gap-1 p-4 rounded-xl border-2 bg-blue-50 border-blue-200 hover:border-blue-400 hover:shadow-md transition-all active:scale-95"
                    data-testid={`sv-msg-group-${g.group_code}`}
                  >
                    <span className="text-sm font-bold text-blue-800">{g.group_code}</span>
                    <span className="text-[10px] text-muted-foreground">{g.message_count} messages</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {currentView === "messages" && msgSelectedGroup && (
          <div className="space-y-4" data-testid="sv-messages-detail">
            <ThreadedMessagesInbox
              token={token}
              groupCode={msgSelectedGroup}
              groupName={msgSelectedGroup}
              campers={campers.filter(c => (c.group_code || "").toUpperCase() === msgSelectedGroup.toUpperCase())}
              onBack={() => { setMsgSelectedGroup(null); setMsgMessages([]); setMsgCamperFilter(""); setMsgDateFrom(""); setMsgDateTo(""); setMsgSelectedCamperIds(new Set()); }}
            />
          </div>
        )}

        {/* ═══ NAVIGATOR INTRAMURALS VIEW ═══ */}
        {currentView === "nav-intramurals" && (
          <NavigatorIntramuralsLanding headers={{ Authorization: `Bearer ${token}` }} onBack={() => setCurrentView("menu")} isEditable={true} />
        )}

        {/* ═══ TRIP ROSTERS (Overnight & Broadway) — Navigator supervisor ═══ */}
        {currentView === "trip-rosters" && (
          <TripRostersView
            data={tripRostersData}
            loading={tripRostersLoading}
            onBack={() => setCurrentView("menu")}
            headerSubtitle="Navigator trip rosters"
          />
        )}

        {/* ═══ SPECIALIST INCIDENT REPORTS — Specialty supervisor inbox ═══ */}
        {currentView === "specialist-reports" && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => setCurrentView("menu")} data-testid="specialist-reports-back">
                <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
              </Button>
              <div>
                <h2 className="text-base font-bold text-rose-700 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" /> Specialist Incident Reports
                </h2>
                <p className="text-xs text-muted-foreground">Last 60 days · {specialistReports.length} report{specialistReports.length === 1 ? "" : "s"}</p>
              </div>
            </div>

            {specialistReportsLoading ? (
              <div className="text-center py-10 text-muted-foreground">Loading…</div>
            ) : specialistReports.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground" data-testid="specialist-reports-empty">
                No specialist incident reports yet.
              </div>
            ) : (
              <div className="space-y-2" data-testid="specialist-reports-list">
                {specialistReports.map((r) => (
                  <div key={r.id} className="p-3 border rounded-lg bg-white hover:border-rose-300" data-testid={`specialist-report-${r.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-bold text-gray-900">{(r.camper_names || []).join(", ") || "—"}</span>
                          {r.specialty && (
                            <span className="text-[10px] font-bold uppercase bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded">
                              {r.specialty}
                            </span>
                          )}
                          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${(r.incident_type || "").toLowerCase() === "medical" ? "bg-rose-100 text-rose-800" : "bg-amber-100 text-amber-800"}`}>
                            {r.incident_type || "Incident"}
                          </span>
                        </div>
                        <p className="text-xs text-gray-700 mt-1 line-clamp-2">{r.description || "(no description)"}</p>
                        <div className="text-[11px] text-muted-foreground mt-1 flex gap-3 flex-wrap">
                          <span>{r.date}</span>
                          {r.camper_group_codes?.length > 0 && (
                            <span>Groups: {r.camper_group_codes.join(", ")}</span>
                          )}
                          <span>Filed {new Date(r.submitted_at).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {currentView === "mariner-intramurals" && (
          <MarinerIntramuralsLanding token={token} onBack={() => setCurrentView("menu")} />
        )}

        {/* ═══ COLOR WAR VIEW ═══ */}
        {currentView === "color-war" && (
          <SupervisorColorWarView token={token} />
        )}

        {/* ═══ SCHEDULES VIEW ═══ */}
        {currentView === "schedules" && (
          <SchedulesPicker
            title={`${divisionName} Schedules`}
            groupCodes={groups.map(g => g.code)}
            adminMode={false}
            onBack={() => setCurrentView("menu")}
          />
        )}

        {/* ═══ LOGINS VIEW ═══ */}
        {currentView === "logins" && (
          <LoginsView onBack={() => setCurrentView("menu")} />
        )}

        {currentView === "group-logins" && (
          <SupervisorGroupLogins token={token} onBack={() => setCurrentView("menu")} />
        )}

        {editingReport && (
          <IncidentReportEditor
            report={editingReport}
            token={token}
            onClose={() => setEditingReport(null)}
            onSaved={(updated) => {
              setIncidentReports(prev => prev.map(r => r.id === updated.id ? { ...r, ...updated } : r));
              setSpecialistReports(prev => prev.map(r => r.id === updated.id ? { ...r, ...updated } : r));
            }}
          />
        )}

      </main>
    </div>
  );
}

/* ═══ Hobbies Reports View ═══ */
function HobbiesReportsView({
  hobbiesSubView, setHobbiesSubView,
  hobbiesWeek, setHobbiesWeek,
  hobbiesReport, hobbiesHistory,
  hobbiesLoading, hobbiesHistoryFilter, setHobbiesHistoryFilter,
  fetchHobbiesReport, fetchHobbiesHistory,
  hobbiesStats,
  hobbiesProgram,
  onDownloadHobbiesPDF,
}) {
  const isNav = hobbiesProgram === "navigator";
  const isIntensive = hobbiesProgram === "intensive";
  const weekMin = isNav ? 2 : 1;
  const weekMax = isNav ? 8 : 6;
  const weekNums = Array.from({ length: weekMax - weekMin + 1 }, (_, i) => weekMin + i);
  const dayLabel = isNav ? "Monday" : "Wednesday";
  const downloadScope = isIntensive
    ? "navigator_intensives"
    : isNav
      ? "navigator_hobbies"
      : "mariner_all";

  const handleWeekNav = (dir) => {
    const next = hobbiesWeek + dir;
    if (next >= weekMin && next <= weekMax) {
      setHobbiesWeek(next);
      fetchHobbiesReport(next);
    }
  };

  const handleExportCSV = () => {
    if (!hobbiesHistory.length) return;
    const header = ["Camper", "Group", "Division", ...weekNums.map(w => `Wk ${w}`)];
    const rows = [header];
    hobbiesHistory.forEach(c => {
      rows.push([c.camper_name, c.group, c.division, ...weekNums.map(w => c.weeks[String(w)] || "")]);
    });
    const csv = rows.map(r => r.map(v => `"${(v || "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${isNav ? "navigator" : "mariner"}_hobbies_history_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const renderActivityList = (activitiesMap, label, timeBlock) => {
    const entries = Object.entries(activitiesMap || {});
    if (entries.length === 0) return null;
    return (
      <div className="space-y-3 print-section" data-testid={`hobbies-activity-list-${label.toLowerCase()}`}>
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-fuchsia-700 uppercase">{label} — {timeBlock}</h3>
          <Badge variant="outline" className="text-xs">
            {entries.reduce((sum, [, campers]) => sum + campers.length, 0)} total selections
          </Badge>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {entries.map(([activity, campers]) => (
            <div key={activity} className="border rounded-xl bg-white overflow-hidden" data-testid={`hobbies-activity-card-${activity.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="px-3 py-2 bg-fuchsia-50 border-b flex items-center justify-between">
                <span className="text-sm font-semibold text-fuchsia-800">{activity}</span>
                <Badge className="bg-fuchsia-100 text-fuchsia-700 border-fuchsia-300 text-xs">{campers.length}</Badge>
              </div>
              <div className="p-2">
                {campers.length === 0 ? (
                  <p className="text-xs text-muted-foreground py-1 px-2">No campers assigned</p>
                ) : (
                  <div className="space-y-0.5">
                    {campers.sort((a, b) => a.group.localeCompare(b.group) || a.camper_name.localeCompare(b.camper_name)).map((c, i) => (
                      <div key={i} className="flex items-center justify-between px-2 py-1 rounded-md hover:bg-muted/30 text-xs">
                        <span className="font-medium">{c.camper_name}</span>
                        <Badge variant="secondary" className="text-[10px]">{c.group}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const filteredHistory = hobbiesHistory.filter(c => {
    if (hobbiesHistoryFilter === "boys") return c.division && c.division.includes("B");
    if (hobbiesHistoryFilter === "girls") return c.division && c.division.includes("G");
    return true;
  });

  return (
    <div className="space-y-4" data-testid="hobbies-reports-view">
      {/* Tabs */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => { setHobbiesSubView("weekly"); if (!hobbiesReport) fetchHobbiesReport(hobbiesWeek); }}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${hobbiesSubView === "weekly" ? "bg-fuchsia-600 text-white shadow-md" : "bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200 hover:bg-fuchsia-100"}`}
          data-testid="hobbies-tab-weekly"
        >
          Weekly Reports
        </button>
        <button
          onClick={() => { setHobbiesSubView("history"); fetchHobbiesHistory(hobbiesHistoryFilter); }}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${hobbiesSubView === "history" ? "bg-fuchsia-600 text-white shadow-md" : "bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200 hover:bg-fuchsia-100"}`}
          data-testid="hobbies-tab-history"
        >
          Full History
        </button>
      </div>

      {/* Popularity Stats */}
      {hobbiesStats && (
        <div className="border rounded-xl bg-white overflow-hidden" data-testid="hobbies-popularity-stats">
          <div className="px-4 py-3 bg-gradient-to-r from-fuchsia-50 to-violet-50 border-b flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-fuchsia-600" />
              <span className="text-sm font-bold text-fuchsia-800">Activity Popularity</span>
            </div>
            <Badge variant="outline" className="text-xs border-fuchsia-300 text-fuchsia-700">
              {hobbiesStats.total_selections} total pick{hobbiesStats.total_selections !== 1 ? "s" : ""}
            </Badge>
          </div>
          <div className="p-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Boys stats */}
            <div data-testid="hobbies-stats-boys">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-blue-700 uppercase">Boys</span>
                <span className="text-[10px] text-muted-foreground">{hobbiesStats.total_campers_boys} campers</span>
              </div>
              <div className="space-y-1.5">
                {(hobbiesStats.activities_boys || []).map(act => {
                  const count = hobbiesStats.boys?.[act] || 0;
                  const maxCount = Math.max(1, ...Object.values(hobbiesStats.boys || {}));
                  const pct = maxCount > 0 ? (count / maxCount) * 100 : 0;
                  return (
                    <div key={act} className="flex items-center gap-2" data-testid={`stat-boys-${act.replace(/\s+/g, "-").toLowerCase()}`}>
                      <span className="text-xs w-24 truncate text-right text-slate-600">{act}</span>
                      <div className="flex-1 h-5 bg-slate-100 rounded-full overflow-hidden relative">
                        <div
                          className="h-full rounded-full transition-all duration-500 bg-gradient-to-r from-blue-400 to-blue-600"
                          style={{ width: `${Math.max(pct, count > 0 ? 8 : 0)}%` }}
                        />
                        {count > 0 && (
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-bold text-blue-800">{count}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Girls stats */}
            <div data-testid="hobbies-stats-girls">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-pink-700 uppercase">Girls</span>
                <span className="text-[10px] text-muted-foreground">{hobbiesStats.total_campers_girls} campers</span>
              </div>
              <div className="space-y-1.5">
                {(hobbiesStats.activities_girls || []).map(act => {
                  const count = hobbiesStats.girls?.[act] || 0;
                  const maxCount = Math.max(1, ...Object.values(hobbiesStats.girls || {}));
                  const pct = maxCount > 0 ? (count / maxCount) * 100 : 0;
                  return (
                    <div key={act} className="flex items-center gap-2" data-testid={`stat-girls-${act.replace(/\s+/g, "-").toLowerCase()}`}>
                      <span className="text-xs w-24 truncate text-right text-slate-600">{act}</span>
                      <div className="flex-1 h-5 bg-slate-100 rounded-full overflow-hidden relative">
                        <div
                          className="h-full rounded-full transition-all duration-500 bg-gradient-to-r from-pink-400 to-pink-600"
                          style={{ width: `${Math.max(pct, count > 0 ? 8 : 0)}%` }}
                        />
                        {count > 0 && (
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-bold text-pink-800">{count}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          {/* Week-by-week mini chart */}
          <div className="px-4 pb-3 border-t pt-3">
            <span className="text-xs font-bold text-muted-foreground uppercase mb-2 block">Picks by Week</span>
            <div className="flex items-end gap-2 h-10">
              {weekNums.map(w => {
                const wkCount = hobbiesStats.by_week?.[String(w)] || 0;
                const maxWk = Math.max(1, ...Object.values(hobbiesStats.by_week || {}));
                const hPct = maxWk > 0 ? (wkCount / maxWk) * 100 : 0;
                return (
                  <div key={w} className="flex-1 flex flex-col items-center gap-0.5" data-testid={`stat-week-${w}`}>
                    <div className="w-full bg-slate-100 rounded-t-md relative" style={{ height: "32px" }}>
                      <div
                        className="absolute bottom-0 w-full rounded-t-md bg-gradient-to-t from-fuchsia-500 to-fuchsia-300 transition-all duration-500"
                        style={{ height: `${Math.max(hPct, wkCount > 0 ? 12 : 0)}%` }}
                      />
                    </div>
                    <span className="text-[9px] font-medium text-muted-foreground">Wk{w}</span>
                    <span className="text-[10px] font-bold text-fuchsia-700">{wkCount}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {hobbiesLoading && (
        <div className="text-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-fuchsia-600 mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading hobbies data...</p>
        </div>
      )}

      {/* Weekly Report */}
      {hobbiesSubView === "weekly" && !hobbiesLoading && (
        <div className="space-y-4">
          {/* Week navigator */}
          <div className="flex items-center justify-between bg-white border rounded-xl p-3">
            <button onClick={() => handleWeekNav(-1)} disabled={hobbiesWeek <= weekMin} className="p-2 rounded-lg hover:bg-muted/50 disabled:opacity-30 transition-colors" data-testid="hobbies-week-prev">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="text-center">
              <p className="text-lg font-bold text-fuchsia-800" data-testid="hobbies-week-label">Week {hobbiesWeek}</p>
              {hobbiesReport && (
                <div className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                  <span>{dayLabel} {new Date((hobbiesReport.day_date || hobbiesReport.wednesday_date) + "T12:00:00").toLocaleDateString("en-US", { timeZone: "America/New_York", month: "long", day: "numeric", year: "numeric" })}</span>
                  <Badge variant="outline" className={`text-[10px] ml-1 ${hobbiesReport.status === "open" ? "border-green-300 text-green-700 bg-green-50" : hobbiesReport.status === "past" ? "border-gray-300 text-gray-600" : "border-violet-300 text-violet-600 bg-violet-50"}`}>
                    {hobbiesReport.status}
                  </Badge>
                </div>
              )}
            </div>
            <button onClick={() => handleWeekNav(1)} disabled={hobbiesWeek >= weekMax} className="p-2 rounded-lg hover:bg-muted/50 disabled:opacity-30 transition-colors" data-testid="hobbies-week-next">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Print / Download buttons */}
          <div className="flex justify-end gap-2 flex-wrap">
            {hobbiesProgram === "mariner" ? (
              <>
                <Button
                  size="sm"
                  onClick={() => onDownloadHobbiesPDF && onDownloadHobbiesPDF("mariner_boys", hobbiesWeek)}
                  className="gap-1.5 bg-blue-600 hover:bg-blue-700"
                  data-testid="hobbies-download-pdf-boys-btn"
                >
                  <Download className="w-3.5 h-3.5" /> Download Boys PDF
                </Button>
                <Button
                  size="sm"
                  onClick={() => onDownloadHobbiesPDF && onDownloadHobbiesPDF("mariner_girls", hobbiesWeek)}
                  className="gap-1.5 bg-pink-600 hover:bg-pink-700"
                  data-testid="hobbies-download-pdf-girls-btn"
                >
                  <Download className="w-3.5 h-3.5" /> Download Girls PDF
                </Button>
              </>
            ) : (
              <Button
                size="sm"
                onClick={() => onDownloadHobbiesPDF && onDownloadHobbiesPDF(downloadScope, hobbiesWeek)}
                className="gap-1.5 bg-fuchsia-600 hover:bg-fuchsia-700"
                data-testid="hobbies-download-pdf-btn"
              >
                <Download className="w-3.5 h-3.5" /> Download PDF
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => window.print()} className="gap-1.5" data-testid="hobbies-print-btn">
              <Printer className="w-3.5 h-3.5" /> Print Report
            </Button>
          </div>

          {hobbiesReport && (
            <div className="space-y-6">
              {renderActivityList(hobbiesReport.by_activity_boys, "Boys", hobbiesReport.boys_time)}
              {renderActivityList(hobbiesReport.by_activity_girls, "Girls", hobbiesReport.girls_time)}

              {/* Raw selections count */}
              <div className="text-xs text-muted-foreground text-center py-2 border-t">
                {(hobbiesReport.selections || []).length} total selection{(hobbiesReport.selections || []).length !== 1 ? "s" : ""} for Week {hobbiesWeek}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Full History Grid */}
      {hobbiesSubView === "history" && !hobbiesLoading && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <button onClick={() => { setHobbiesHistoryFilter("all"); fetchHobbiesHistory("all"); }} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${hobbiesHistoryFilter === "all" ? "bg-fuchsia-600 text-white" : "bg-muted/50 hover:bg-muted"}`} data-testid="hobbies-filter-all">All</button>
              <button onClick={() => { setHobbiesHistoryFilter("boys"); fetchHobbiesHistory("boys"); }} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${hobbiesHistoryFilter === "boys" ? "bg-blue-600 text-white" : "bg-muted/50 hover:bg-muted"}`} data-testid="hobbies-filter-boys">Boys</button>
              <button onClick={() => { setHobbiesHistoryFilter("girls"); fetchHobbiesHistory("girls"); }} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${hobbiesHistoryFilter === "girls" ? "bg-pink-600 text-white" : "bg-muted/50 hover:bg-muted"}`} data-testid="hobbies-filter-girls">Girls</button>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => window.print()} className="gap-1.5" data-testid="hobbies-history-print-btn">
                <Printer className="w-3.5 h-3.5" /> Print
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportCSV} className="gap-1.5" data-testid="hobbies-export-csv-btn">
                <Download className="w-3.5 h-3.5" /> Export CSV
              </Button>
            </div>
          </div>

          {filteredHistory.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Sparkles className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>No hobbies history available</p>
            </div>
          ) : (
            <div className="overflow-x-auto border rounded-xl bg-white">
              <table className="w-full text-xs" data-testid="hobbies-history-table">
                <thead>
                  <tr className="bg-fuchsia-50 border-b">
                    <th className="text-left px-3 py-2.5 font-semibold text-fuchsia-800 sticky left-0 bg-fuchsia-50 min-w-[160px]">Camper</th>
                    <th className="text-center px-2 py-2.5 font-semibold text-fuchsia-800 min-w-[60px]">Group</th>
                    {weekNums.map(w => (
                      <th key={w} className="text-center px-2 py-2.5 font-semibold text-fuchsia-800 min-w-[100px]">Wk {w}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredHistory.map((row, idx) => (
                    <tr key={row.camper_id} className={`border-b last:border-0 ${idx % 2 === 0 ? "bg-white" : "bg-muted/20"}`} data-testid={`hobbies-history-row-${row.camper_id}`}>
                      <td className="px-3 py-2 font-medium sticky left-0 bg-inherit">{row.camper_name}</td>
                      <td className="text-center px-2 py-2"><Badge variant="secondary" className="text-[10px]">{row.group}</Badge></td>
                      {weekNums.map(w => {
                        const val = row.weeks[String(w)];
                        const isActivity = val && val !== "\u2014" && val !== "Pending" && val !== "No Selection";
                        return (
                          <td key={w} className="text-center px-2 py-2">
                            {isActivity ? (
                              <span className="inline-block px-2 py-0.5 rounded-full bg-fuchsia-100 text-fuchsia-700 font-medium text-[10px]">{val}</span>
                            ) : val === "Pending" ? (
                              <span className="text-amber-500 font-medium">Pending</span>
                            ) : val === "No Selection" ? (
                              <span className="text-red-400">Missed</span>
                            ) : (
                              <span className="text-muted-foreground">{val}</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-xs text-muted-foreground text-center">{filteredHistory.length} camper{filteredHistory.length !== 1 ? "s" : ""}</p>
        </div>
      )}
    </div>
  );
}

/* ═══ Camper Card ═══ */
function SvCamperCard({ camper, onClick }) {
  const initials = `${camper.first_name?.[0] || ""}${camper.last_name?.[0] || ""}`.toUpperCase();
  const displayName = camper.first_name;
  const sessionTags = (camper.sessions || []).map(s => SESSION_SHORT_LABELS[s.name] || s.name).filter(Boolean);
  const hasAlerts = hasMedAlerts(camper);
  const isNursery = (camper.group_code || "").toUpperCase().startsWith("N");

  return (
    <div onClick={onClick} className="group relative overflow-hidden rounded-xl border bg-white shadow-sm hover:shadow-lg transition-all duration-200 hover:-translate-y-1 cursor-pointer active:scale-[0.98]" data-testid={`sv-camper-card-${camper.id}`}>
      {hasAlerts && (
        <div className="absolute top-0 right-0 w-0 h-0 border-t-[36px] border-t-red-500 border-l-[36px] border-l-transparent">
          <AlertTriangle className="absolute -top-8 right-0.5 w-3.5 h-3.5 text-white" />
        </div>
      )}
      <div className="p-4">
        <div className="flex items-start gap-3">
          <CamperPhoto
            camperId={camper.id}
            firstName={camper.first_name}
            lastName={camper.last_name}
            size={44}
            className="flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <h3 className="font-heading font-semibold text-sm truncate">{displayName} {camper.last_name}</h3>
            <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
              {camper.group_code && <Badge variant="secondary" className="text-xs bg-indigo-50 text-indigo-700 border-indigo-200">{camper.cit_subgroup || camper.group_code}</Badge>}
              {camper.age && <span className="text-xs text-muted-foreground">Age {Math.floor(camper.age)}</span>}
              {isNursery ? (
                <Badge variant="outline" className="text-xs border-0 px-1.5"><span className="w-2.5 h-2.5 rounded-full inline-block mr-1 bg-green-500" />Kiddie</Badge>
              ) : camper.swim_color ? (
                <Badge variant="outline" className="text-xs border-0 px-1.5">
                  <span className={`w-2.5 h-2.5 rounded-full inline-block mr-1 ${swimDot(camper.swim_color)}`} />
                  {camper.swim_color}
                </Badge>
              ) : null}
              {sessionTags.map(tag => <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>)}
            </div>
            {camper.lunch_order && camper.lunch_date === new Date().toISOString().split("T")[0] && (
              <div className="flex items-center gap-1.5 mt-1.5 px-2.5 py-1.5 bg-amber-50 border border-amber-200 rounded-lg">
                <Utensils className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
                <span className="text-xs font-medium text-amber-800 truncate">{camper.lunch_order}</span>
              </div>
            )}
            {(camper.am_bus || camper.pm_bus || camper.am_method || camper.pm_method || camper.pm_weekly_today) && (
              <div className="flex flex-col gap-1 mt-1.5 px-2.5 py-1.5 bg-green-50 border border-green-200 rounded-lg" data-testid="sv-camper-transport">
                <div className="flex items-center gap-2">
                  <Bus className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                  <span className="text-xs font-medium text-green-800">
                    {camper.am_bus && <>AM: {camper.am_bus}</>}
                    {camper.am_bus && (camper.pm_bus || camper.pm_weekly_today) && <span className="mx-1 text-green-400">|</span>}
                    {camper.pm_weekly_today ? (
                      <>
                        PM: {camper.pm_weekly_location}{camper.pm_weekly_remove ? " (not on pickup list)" : ""}
                        <span className="ml-1 text-[9px] font-bold uppercase px-1 py-px rounded bg-emerald-200 text-emerald-800 border border-emerald-300 align-middle" data-testid={`sv-pm-weekly-${camper.id}`}>Weekly</span>
                      </>
                    ) : camper.pm_bus ? <>PM: {camper.pm_bus}</> : null}
                  </span>
                </div>
                {(camper.am_method || camper.pm_method) && (
                  <span className="text-[10px] text-green-600 ml-5">
                    {camper.am_method && <>Drop-off: {camper.am_method}</>}
                    {camper.am_method && camper.pm_method && <> · </>}
                    {camper.pm_method && <>Pickup: {camper.pm_method}</>}
                  </span>
                )}
              </div>
            )}
            <CITCardInline camper={camper} />
          </div>
        </div>
        {hasAlerts && (
          <div className="mt-2 pt-2 border-t">
            <Badge className="text-xs bg-red-50 text-red-700 border-red-200"><AlertTriangle className="w-3 h-3 mr-1" />Medical Alert</Badge>
          </div>
        )}
      </div>
      <div className="px-4 py-2.5 bg-muted/30 border-t text-xs text-muted-foreground flex items-center justify-between">
        <span>View Details</span>
        <span className="opacity-0 group-hover:opacity-100 transition-opacity">&rarr;</span>
      </div>
    </div>
  );
}

/* Compact CIT block rendered INLINE on the daily-roster card.
   Click on the toggle stops propagation so the parent card's onClick
   (open detail dialog) does not fire. */
const CIT_TRIP_DATES_BY_NUM = {
  1: "2026-07-01",
  2: "2026-07-07",
  3: "2026-07-13",
  4: "2026-07-23",
  5: "2026-07-27",
  6: "2026-08-04",
  7: "2026-08-11",
  8: "2026-08-17",
};

function formatCitTripDate(tripName) {
  const m = /^Trip\s+(\d+)/i.exec(tripName || "");
  if (!m) return "";
  const iso = CIT_TRIP_DATES_BY_NUM[parseInt(m[1], 10)];
  if (!iso) return "";
  try {
    return new Date(iso + "T12:00:00").toLocaleDateString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}

function CITCardInline({ camper }) {
  const [open, setOpen] = useState(false);
  const gc = (camper.group_code || "").toUpperCase();
  if (!gc.startsWith("CIT")) return null;

  const ranks = [1, 2, 3];
  const choicePairs = ranks
    .map(r => ({
      rank: r,
      program: camper[`cit_program_${r}`] || "",
      choice: camper[`cit_choice_${r}`] || "",
    }))
    .filter(p => p.program || p.choice);

  const trips = camper.cit_trips || {};
  const enrolledTrips = Object.entries(trips).filter(([, v]) => v && String(v).trim().length > 0);
  const paperwork = camper.cit_parent_signature || "";

  const hasAny = choicePairs.length > 0 || enrolledTrips.length > 0 || paperwork;
  if (!hasAny) return null;

  return (
    <div className="mt-2" data-testid={`cit-card-inline-${camper.id}`}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
        className="w-full flex items-center justify-between gap-1.5 px-2.5 py-1.5 rounded-md border border-fuchsia-200 bg-fuchsia-50 hover:bg-fuchsia-100 transition-colors"
        data-testid={`cit-card-inline-toggle-${camper.id}`}
      >
        <span className="flex items-center gap-1.5 text-xs font-semibold text-fuchsia-800">
          <Star className="w-3 h-3" />
          CIT Info
          {paperwork ? (
            <Badge variant="outline" className="h-4 px-1 text-[9px] border-emerald-300 text-emerald-700 bg-white">✓ Paper</Badge>
          ) : (
            <Badge variant="outline" className="h-4 px-1 text-[9px] border-amber-300 text-amber-700 bg-white">No Paper</Badge>
          )}
          {enrolledTrips.length > 0 && (
            <Badge variant="outline" className="h-4 px-1 text-[9px] border-fuchsia-300 text-fuchsia-700 bg-white">{enrolledTrips.length} trips</Badge>
          )}
        </span>
        {open ? <ChevronUp className="w-3 h-3 text-fuchsia-700" /> : <ChevronDown className="w-3 h-3 text-fuchsia-700" />}
      </button>
      {open && (
        <div
          className="mt-1.5 p-2 rounded-md border border-fuchsia-100 bg-white space-y-1.5"
          onClick={(e) => e.stopPropagation()}
        >
          {choicePairs.length > 0 && (
            <div className="space-y-0.5">
              <p className="text-[9px] font-semibold uppercase tracking-wider text-fuchsia-700">Choices</p>
              {choicePairs.map(p => (
                <div key={p.rank} className="flex items-start gap-1.5 text-[11px] leading-tight">
                  <span className="font-bold text-fuchsia-700">#{p.rank}</span>
                  <span className="text-slate-700">
                    {p.program && <span className="font-medium">{p.program}</span>}
                    {p.program && p.choice && <span className="text-slate-400"> · </span>}
                    {p.choice}
                  </span>
                </div>
              ))}
            </div>
          )}
          {enrolledTrips.length > 0 && (
            <div className="space-y-0.5">
              <p className="text-[9px] font-semibold uppercase tracking-wider text-fuchsia-700">Trips</p>
              <div className="flex flex-wrap gap-1">
                {enrolledTrips.map(([name]) => {
                  const d = formatCitTripDate(name);
                  return (
                    <Badge key={name} variant="outline" className="h-4 px-1 text-[9px] border-fuchsia-300 text-fuchsia-700 bg-fuchsia-50">
                      {name}{d ? ` · ${d}` : ""}
                    </Badge>
                  );
                })}
              </div>
            </div>
          )}
          {paperwork && (
            <p className="text-[10px] text-slate-600"><span className="font-semibold">Paperwork:</span> {paperwork}</p>
          )}
        </div>
      )}
    </div>
  );
}

function swimDot(color) {
  const c = (color || "").toLowerCase();
  if (c.includes("red") && c.includes("blue")) return "bg-gradient-to-r from-red-500 to-blue-500";
  if (c.includes("yellow") && c.includes("blue")) return "bg-gradient-to-r from-yellow-400 to-blue-500";
  if (c.includes("green") && c.includes("blue")) return "bg-gradient-to-r from-green-500 to-blue-500";
  if (c === "red") return "bg-red-500";
  if (c === "yellow") return "bg-yellow-400";
  if (c === "blue") return "bg-blue-500";
  if (c === "green") return "bg-green-500";
  return "bg-gray-300";
}

function hasMedAlerts(camper) {
  const md = camper.medical_data || {};
  if (md.Medical) {
    return Object.entries(md.Medical).some(([k, v]) => {
      if (!v) return false;
      const vl = v.toString().toLowerCase();
      return (k.toLowerCase().includes("allerg") || k.toLowerCase().includes("reaction"))
        && vl !== "no" && vl !== "none" && vl !== "n/a" && vl !== "";
    });
  }
  return false;
}

/* ═══ CIT Info Block (Collapsible) ═══
   Renders a collapsible "CIT Info" section showing the camper's program/choice
   preferences, enrolled CIT trips (with dates), and parent paperwork status.
   Only renders when the camper is in a CIT group. */
function CITInfoBlock({ camper }) {
  const [open, setOpen] = useState(false);
  const gc = (camper.group_code || "").toUpperCase();
  if (!gc.startsWith("CIT")) return null;

  const ranks = [1, 2, 3];
  const choicePairs = ranks
    .map(r => ({
      rank: r,
      program: camper[`cit_program_${r}`] || "",
      choice: camper[`cit_choice_${r}`] || "",
    }))
    .filter(p => p.program || p.choice);

  const trips = camper.cit_trips || {};
  const tripDates = Array.isArray(camper.cit_trip_dates) ? camper.cit_trip_dates : [];
  const enrolledTrips = Object.entries(trips).filter(([, v]) => v && String(v).trim().length > 0);

  const paperwork = camper.cit_parent_signature || "";
  const lastYear = camper.cit_last_year_position || "";
  const gossip = camper.cit_gossip_signed;

  const hasAny = choicePairs.length > 0 || enrolledTrips.length > 0 || tripDates.length > 0 || paperwork || lastYear || gossip != null;
  if (!hasAny) return null;

  return (
    <section data-testid="cit-info-section">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-fuchsia-200 bg-fuchsia-50 hover:bg-fuchsia-100 transition-colors"
        data-testid="cit-info-toggle"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-fuchsia-800">
          <Star className="w-4 h-4" />
          CIT Info
          <Badge variant="outline" className="text-[10px] border-fuchsia-300 text-fuchsia-700 bg-white">
            {camper.cit_subgroup || gc}
          </Badge>
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-fuchsia-700" /> : <ChevronDown className="w-4 h-4 text-fuchsia-700" />}
      </button>
      {open && (
        <div className="mt-2 grid gap-3 p-3 rounded-lg border border-fuchsia-100 bg-white">
          {choicePairs.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-fuchsia-700 mb-1.5">Program / Choice Preferences</p>
              <div className="grid gap-1.5">
                {choicePairs.map(p => (
                  <div key={p.rank} className="flex items-center gap-2 text-sm">
                    <Badge variant="secondary" className="text-[10px] bg-fuchsia-100 text-fuchsia-800">#{p.rank}</Badge>
                    <span className="font-medium text-slate-800">{p.program || "—"}</span>
                    {p.choice && <span className="text-slate-500">·</span>}
                    {p.choice && <span className="text-slate-700">{p.choice}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
          {enrolledTrips.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-fuchsia-700 mb-1.5">CIT Trips ({enrolledTrips.length})</p>
              <div className="flex flex-wrap gap-1.5">
                {enrolledTrips.map(([name, dateVal]) => {
                  const d = formatCitTripDate(name) || (typeof dateVal === "string" ? dateVal : "");
                  return (
                    <Badge key={name} variant="outline" className="text-xs border-fuchsia-300 text-fuchsia-700 bg-fuchsia-50">
                      {name}{d ? ` · ${d}` : ""}
                    </Badge>
                  );
                })}
              </div>
            </div>
          )}
          {tripDates.length > 0 && enrolledTrips.length === 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-fuchsia-700 mb-1.5">CIT Trip Dates</p>
              <div className="flex flex-wrap gap-1.5">
                {tripDates.map(d => (
                  <Badge key={d} variant="outline" className="text-xs border-fuchsia-300 text-fuchsia-700 bg-fuchsia-50">{d}</Badge>
                ))}
              </div>
            </div>
          )}
          <div className="grid gap-1.5 text-xs">
            {paperwork && (
              <div className="flex items-start gap-2">
                <CheckCircle className="w-3.5 h-3.5 text-emerald-600 mt-0.5 flex-shrink-0" />
                <span><span className="font-semibold text-slate-700">Paperwork:</span> <span className="text-slate-700">{paperwork}</span></span>
              </div>
            )}
            {!paperwork && (
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-600 mt-0.5 flex-shrink-0" />
                <span className="text-amber-800 font-medium">Parent paperwork not yet submitted</span>
              </div>
            )}
            {lastYear && (
              <div className="text-slate-600"><span className="font-semibold">Last year:</span> {lastYear}</div>
            )}
            {gossip != null && (
              <div className="text-slate-600">
                <span className="font-semibold">Gossip Agreement:</span>{" "}
                {gossip ? <span className="text-emerald-700 font-medium">Signed</span> : <span className="text-amber-700 font-medium">Not signed</span>}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

/* ═══ Camper Detail ═══ */
function SvCamperDetail({ camper, onClose, onDownloadPDF, readOnly }) {
  const initials = `${camper.first_name?.[0] || ""}${camper.last_name?.[0] || ""}`.toUpperCase();
  const medicalData = camper.medical_data || {};
  const parentInfo = camper.parent_info || {};

  const sectionOrder = ["Medical", "Family", "Behavioral", "Additional Info", "Skippers Only", "Swim"];
  const sectionIcons = { Medical: Stethoscope, Family: Heart, Behavioral: Brain, "Additional Info": Star, "Skippers Only": FileText, Swim: Waves };
  const sectionColors = { Medical: "text-red-700", Family: "text-blue-700", Behavioral: "text-purple-700", "Additional Info": "text-green-700", "Skippers Only": "text-orange-700", Swim: "text-cyan-700" };

  return (
    <>
      <DialogHeader className="p-4 sm:p-6 pb-0">
        <div className="flex items-start gap-4">
          <CamperPhoto
            camperId={camper.id}
            firstName={camper.first_name}
            lastName={camper.last_name}
            size={80}
            className="flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <DialogTitle className="font-heading text-xl sm:text-2xl text-indigo-700">
              {camper.first_name} {camper.last_name}
            </DialogTitle>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              {camper.group_code && <Badge variant="secondary" className="bg-indigo-50 text-indigo-700 text-xs"><Users className="w-3 h-3 mr-1" />{camper.group_name || camper.group_code}</Badge>}
              {camper.age && <Badge variant="outline" className="text-xs"><Calendar className="w-3 h-3 mr-1" />Age {Math.floor(camper.age)}</Badge>}
              {camper.gender && <Badge variant="outline" className="text-xs"><User className="w-3 h-3 mr-1" />{camper.gender}</Badge>}
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => onDownloadPDF(camper)} data-testid="sv-download-pdf">
            <Download className="w-4 h-4 mr-2" />PDF
          </Button>
        </div>
      </DialogHeader>
      <ScrollArea className="max-h-[60vh]">
        <div className="p-4 sm:p-6 space-y-5">
          {(parentInfo.name || parentInfo.phone || parentInfo.email) && (
            <section>
              <h3 className="font-heading font-semibold flex items-center gap-2 mb-3 text-indigo-700"><User className="w-4 h-4" />Parent / Guardian</h3>
              <div className="grid gap-2">
                {parentInfo.name && <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-100"><User className="w-4 h-4 text-blue-600" /><div><p className="text-xs text-muted-foreground">Name</p><p className="text-sm font-medium">{parentInfo.name}</p></div></div>}
                {parentInfo.phone && <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-100"><Phone className="w-4 h-4 text-blue-600" /><div><p className="text-xs text-muted-foreground">Phone</p><p className="text-sm font-medium">{formatPhone(parentInfo.phone)}</p></div></div>}
                {parentInfo.email && <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-100"><Mail className="w-4 h-4 text-blue-600" /><div><p className="text-xs text-muted-foreground">Email</p><p className="text-sm font-medium break-all">{parentInfo.email}</p></div></div>}
              </div>
            </section>
          )}

          {camper.sessions?.length > 0 && (
            <section>
              <h3 className="font-heading font-semibold flex items-center gap-2 mb-3 text-indigo-700"><CalendarDays className="w-4 h-4" />Program</h3>
              <div className="grid gap-2">
                {camper.sessions.map((s, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                    <span className="text-sm font-medium text-emerald-800">{s.name}</span>
                    <span className="text-xs text-emerald-600">
                      {new Date(s.start + "T00:00:00").toLocaleDateString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric" })} – {new Date(s.end + "T00:00:00").toLocaleDateString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric" })}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {(camper.am_bus || camper.pm_bus || camper.am_method || camper.pm_method || camper.pm_weekly_today) && (
            <section data-testid="sv-camper-transport-section">
              <h3 className="font-heading font-semibold flex items-center gap-2 mb-3 text-teal-700"><Bus className="w-4 h-4" />Transportation</h3>
              <div className="grid grid-cols-2 gap-2">
                {(camper.am_bus || camper.am_method) && (
                  <div className="p-3 bg-sky-50 rounded-lg border border-sky-200" data-testid="sv-am-transport">
                    <p className="text-xs font-semibold uppercase tracking-wider text-sky-700 mb-1">AM Drop-off</p>
                    {camper.am_bus && (<p className="text-base font-bold text-sky-900">{camper.am_bus}</p>)}
                    {camper.am_method && camper.am_method !== camper.am_bus && (
                      <p className={`${camper.am_bus ? "text-xs text-sky-700 mt-0.5" : "text-base font-bold text-sky-900"}`}>{camper.am_method}</p>
                    )}
                  </div>
                )}
                {(camper.pm_bus || camper.pm_method || camper.pm_weekly_today) && (
                  <div className="p-3 bg-orange-50 rounded-lg border border-orange-200" data-testid="sv-pm-transport">
                    <p className="text-xs font-semibold uppercase tracking-wider text-orange-700 mb-1">PM Pickup</p>
                    {camper.pm_weekly_today ? (
                      <>
                        <p className="text-base font-bold text-orange-900">{camper.pm_weekly_location}</p>
                        {camper.pm_weekly_remove && (<p className="text-xs text-orange-700 mt-0.5">Not on a pickup list today</p>)}
                        <span className="inline-block mt-1 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-emerald-200 text-emerald-800 border border-emerald-300">Weekly schedule</span>
                      </>
                    ) : (
                      <>
                        {camper.pm_bus && (<p className="text-base font-bold text-orange-900">{camper.pm_bus}</p>)}
                        {camper.pm_method && camper.pm_method !== camper.pm_bus && (
                          <p className={`${camper.pm_bus ? "text-xs text-orange-700 mt-0.5" : "text-base font-bold text-orange-900"}`}>{camper.pm_method}</p>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            </section>
          )}

          {(() => {
            const hasSections = typeof Object.values(medicalData)[0] === "object";
            if (hasSections) {
              return sectionOrder.map(section => {
                const data = medicalData[section];
                if (!data || Object.keys(data).length === 0) return null;
                const Icon = sectionIcons[section] || FileText;
                const color = sectionColors[section] || "text-indigo-700";
                return (
                  <section key={section}>
                    <h3 className={`font-heading font-semibold flex items-center gap-2 mb-3 ${color}`}><Icon className="w-4 h-4" />{section}</h3>
                    <div className="grid gap-2">
                      {Object.entries(data).map(([key, value]) => {
                        const isAlert = key.toLowerCase().includes("allerg") || key.toLowerCase().includes("reaction");
                        return (
                          <div key={key} className={`p-3 rounded-lg border ${isAlert && section === "Medical" ? "bg-red-50 border-red-200" : "bg-muted/30"}`}>
                            <p className={`text-xs font-medium uppercase tracking-wider mb-1 ${isAlert && section === "Medical" ? "text-red-600" : "text-muted-foreground"}`}>
                              {isAlert && section === "Medical" && <AlertTriangle className="w-3 h-3 inline mr-1" />}{key}
                            </p>
                            <p className="text-sm">{value || "—"}</p>
                          </div>
                        );
                      })}
                    </div>
                  </section>
                );
              });
            }
            return null;
          })()}

          <CITInfoBlock camper={camper} />

          <SvCamperNotesEditor camperId={camper.id} readOnly={readOnly} />

          <SvCamperIncidentHistory camperId={camper.id} />

          <section className="text-xs text-muted-foreground border-t pt-4">
            <p>Person ID: {camper.person_id}</p>
            {camper.last_synced && <p>Last Synced: {new Date(camper.last_synced).toLocaleString("en-US", { timeZone: "America/New_York" })}</p>}
          </section>
        </div>
      </ScrollArea>
    </>
  );
}

function SvCamperIncidentHistory({ camperId }) {
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(`${API}/campers/${camperId}/incident-reports`, { headers });
        if (!cancelled) setReports(res.data?.reports || []);
      } catch {
        if (!cancelled) setReports([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [camperId]);

  const byYear = reports.reduce((acc, r) => {
    const y = r.camp_year || (r.date ? new Date(r.date).getFullYear() : "Undated");
    (acc[y] = acc[y] || []).push(r);
    return acc;
  }, {});
  const years = Object.keys(byYear).sort((a, b) => Number(b) - Number(a));
  const apiUrl = process.env.REACT_APP_BACKEND_URL;

  return (
    <section className="border-t pt-4" data-testid="camper-incident-history-section">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-2 text-sm font-semibold text-rose-900 hover:text-rose-700"
        data-testid="camper-incident-history-toggle"
      >
        <span className="flex items-center gap-1.5">
          <AlertTriangle className="w-4 h-4 text-rose-600" />
          Incident History
          <Badge variant="secondary" className={`ml-1 h-4 px-1.5 text-[10px] ${reports.length ? "bg-rose-100 text-rose-800" : "bg-slate-200 text-slate-700"}`}>
            {loading ? "…" : reports.length}
          </Badge>
        </span>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {open && (
        <div className="mt-2 space-y-3 max-h-[360px] overflow-y-auto pr-1" data-testid="camper-incident-history-list">
          {loading ? (
            <p className="text-xs text-muted-foreground">Loading…</p>
          ) : reports.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">No incident reports filed for this camper.</p>
          ) : (
            years.map((y) => (
              <div key={y}>
                <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-1">{y} ({byYear[y].length})</p>
                <div className="space-y-1.5">
                  {byYear[y].map((r) => {
                    const isGreatDay = !!r.great_day;
                    return (
                      <div
                        key={r.id}
                        className={`p-2 rounded-md border ${isGreatDay ? "border-emerald-200 bg-emerald-50/60" : "border-rose-200 bg-rose-50/60"}`}
                        data-testid={`camper-incident-row-${r.id}`}
                      >
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                              <Badge variant="outline" className={`text-[10px] ${isGreatDay ? "border-emerald-400 text-emerald-700" : "border-rose-400 text-rose-700"}`}>
                                {r.date || "—"}
                              </Badge>
                              <span className="text-[11px] font-semibold text-slate-800">
                                {isGreatDay ? "🌟 Great Day" : (r.incident_type || "Incident")}
                              </span>
                              {r.followup === "yes" && (
                                <Badge className="text-[10px] bg-amber-200 text-amber-900">Follow-up</Badge>
                              )}
                            </div>
                            <p className="text-[11px] text-slate-600 truncate">
                              {(r.description || r.great_day_notes || "").slice(0, 120) || <em className="text-slate-400">No description</em>}
                            </p>
                            <p className="text-[10px] text-slate-500 mt-0.5">
                              Reporter: {r.counselor || r.specialty || r.submitter_type || "—"}
                            </p>
                          </div>
                          <a
                            href={`${apiUrl}/api/incident-reports/${r.id}/pdf`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="shrink-0 inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold bg-white border border-slate-300 rounded hover:bg-slate-50"
                            data-testid={`camper-incident-pdf-${r.id}`}
                          >
                            <FileText className="w-3 h-3" /> PDF
                          </a>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </section>
  );
}

function SvCamperNotesEditor({ camperId, readOnly }) {
  const [notes, setNotes] = useState("");
  const [draft, setDraft] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [updatedBy, setUpdatedBy] = useState(null);
  const [legacyNotes, setLegacyNotes] = useState([]);
  const [legacyOpen, setLegacyOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API}/campers/${camperId}/notes`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!alive) return;
        setNotes(res.data?.notes || "");
        setDraft(res.data?.notes || "");
        setUpdatedAt(res.data?.updated_at || null);
        setUpdatedBy(res.data?.updated_by || null);
        setLegacyNotes(Array.isArray(res.data?.legacy_notes) ? res.data.legacy_notes : []);
      } catch {
        if (alive) toast.error("Failed to load notes");
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, [camperId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.put(`${API}/campers/${camperId}/notes`, { notes: draft }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotes(res.data?.notes || "");
      setUpdatedAt(res.data?.updated_at || null);
      setUpdatedBy(res.data?.updated_by || null);
      toast.success("Notes saved");
    } catch {
      toast.error("Failed to save notes");
    } finally {
      setSaving(false);
    }
  };

  const dirty = draft !== notes;
  const updatedDisplay = updatedAt
    ? new Date(updatedAt).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })
    : null;

  return (
    <section data-testid="camper-notes-section">
      <h3 className="font-heading font-semibold text-base flex items-center gap-2 mb-2 text-purple-700">
        <FileText className="w-4 h-4" />Notes
        <Badge variant="outline" className="text-[10px] border-purple-300 text-purple-700 bg-purple-50 ml-1">Admin & Supervisors Only</Badge>
        {readOnly && <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700 bg-amber-50 ml-1">Read Only</Badge>}
      </h3>
      {loading ? (
        <p className="text-xs text-muted-foreground italic">Loading notes…</p>
      ) : (
        <div className="space-y-2">
          <textarea
            className="w-full min-h-[110px] p-2.5 text-sm border rounded-lg bg-white focus:ring-2 focus:ring-purple-300 focus:border-purple-400 outline-none resize-y"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Private notes about this camper. Visible to Admin and all 3 division supervisors only."
            data-testid="camper-notes-textarea"
            readOnly={readOnly}
          />
          <div className="flex items-center justify-between gap-2">
            <p className="text-[10px] text-muted-foreground">
              {updatedDisplay
                ? <>Last edited{updatedBy ? <> by <span className="font-semibold">{updatedBy}</span></> : ""} · {updatedDisplay}</>
                : "Not edited yet."}
            </p>
            {!readOnly && (
              <Button size="sm" onClick={handleSave} disabled={saving || !dirty} className="bg-purple-600 hover:bg-purple-700 text-xs h-8" data-testid="camper-notes-save">
                <Save className="w-3 h-3 mr-1" />{saving ? "Saving…" : "Save Notes"}
              </Button>
            )}
          </div>
          {legacyNotes.length > 0 && (
            <div className="mt-3 border-t pt-3" data-testid="legacy-notes-block">
              <button
                type="button"
                onClick={() => setLegacyOpen(v => !v)}
                className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 hover:text-slate-900"
                data-testid="legacy-notes-toggle"
              >
                <FileText className="w-3.5 h-3.5" />
                Legacy Notes (CampMinder &amp; prior seasons)
                <Badge variant="secondary" className="ml-1 h-4 px-1.5 text-[10px] bg-slate-200 text-slate-700">{legacyNotes.length}</Badge>
                {legacyOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              </button>
              {legacyOpen && (
                <div className="mt-2 space-y-2 max-h-[260px] overflow-y-auto pr-1">
                  {legacyNotes.map((ln, i) => (
                    <div key={i} className="p-2 rounded-md border border-slate-200 bg-slate-50/60" data-testid={`legacy-note-${i}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="text-[10px] border-slate-300 text-slate-700 bg-white">
                          {ln.year ? ln.year : "Undated"}
                        </Badge>
                        <span className="text-[10px] text-slate-500">{ln.source}</span>
                      </div>
                      <p className="text-xs text-slate-700 whitespace-pre-wrap break-words">{ln.content}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

export const SUPERVISOR_FOLLOWUP_ACTIONS = [
  "Spoke to Parent",
  "Spoke to Camper",
  "Spoke to Counselor",
  "Spoke to Specialist",
  "Spoke to Director",
  "Situation Resolved",
];

export const INCIDENT_ACTIONS_TAKEN = ["Sent to Nurse", "Supervisor Notified", "Parent Contacted"];
export const INCIDENT_FOLLOWUP_OPTIONS = ["Situation Resolved", "Parent Follow-Up Completed"];

export function IncidentReportEditor({ report, token, onSaved, onClose }) {
  const isSpecialist = report.submitter_type === "specialist";
  const [form, setForm] = useState({
    date: report.date || "",
    great_day: !!report.great_day,
    great_day_notes: report.great_day_notes || "",
    incident_type: report.incident_type || "",
    description: report.description || "",
    actions_taken: Array.isArray(report.actions_taken) ? [...report.actions_taken] : [],
    parent_contact_detail: report.parent_contact_detail || "",
    action_details: report.action_details || "",
    followup: Array.isArray(report.followup) ? [...report.followup] : [],
    followup_details: report.followup_details || "",
    specialty: report.specialty || "",
  });
  const [saving, setSaving] = useState(false);

  const toggle = (field, value) => {
    setForm(prev => {
      const arr = prev[field] || [];
      return { ...prev, [field]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value] };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.patch(
        `${API}/incident-reports/${report.id}`,
        form,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.no_changes) {
        toast.info("No changes to save");
      } else {
        toast.success(`Report updated (${(res.data?.fields_changed || []).length} field${(res.data?.fields_changed || []).length === 1 ? "" : "s"})`);
      }
      onSaved?.({ ...report, ...form, last_edit_at: res.data?.edit?.edited_at, last_edit_by: res.data?.edit?.editor_label });
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update report");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={`incident-edit-modal-${report.id}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-rose-600" />
            Edit Incident Report
          </DialogTitle>
          <DialogDescription>
            Reporter, submission timestamp, and AI summary stay locked. All edits are logged.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-2 text-[11px] text-slate-600">
            <span className="font-semibold">Originally filed:</span> {report.counselor || report.submitter_type} ·{" "}
            <span className="font-semibold">Group:</span> {report.group_code} ·{" "}
            <span className="font-semibold">Submitted:</span> {report.submitted_at ? new Date(report.submitted_at).toLocaleString("en-US", { timeZone: "America/New_York" }) : "—"}
            {report.last_edit_at && (
              <span className="block mt-1 text-amber-700">
                Last edited by <strong>{report.last_edit_by || "Supervisor"}</strong> · {new Date(report.last_edit_at).toLocaleString("en-US", { timeZone: "America/New_York" })}
              </span>
            )}
          </div>

          <div>
            <Label className="text-xs font-semibold">Date of Incident</Label>
            <Input
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              className="mt-1"
              data-testid={`incident-edit-date-${report.id}`}
            />
          </div>

          {isSpecialist && (
            <div>
              <Label className="text-xs font-semibold">Specialty</Label>
              <Input
                value={form.specialty}
                onChange={(e) => setForm({ ...form, specialty: e.target.value })}
                placeholder="e.g., Tennis, Arts, Music"
                className="mt-1"
                data-testid={`incident-edit-specialty-${report.id}`}
              />
            </div>
          )}

          {!form.great_day && (
            <>
              <div>
                <Label className="text-xs font-semibold">Incident Type</Label>
                <Select value={form.incident_type || ""} onValueChange={(v) => setForm({ ...form, incident_type: v })}>
                  <SelectTrigger className="mt-1" data-testid={`incident-edit-type-${report.id}`}>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Medical">Medical</SelectItem>
                    <SelectItem value="Behavioral">Behavioral</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs font-semibold">Description</Label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full min-h-[90px] mt-1 p-2 text-sm border rounded-lg focus:ring-2 focus:ring-rose-300 focus:border-rose-400 outline-none resize-y"
                  data-testid={`incident-edit-description-${report.id}`}
                />
              </div>

              <div>
                <Label className="text-xs font-semibold">Actions Taken</Label>
                <div className="flex flex-wrap gap-3 mt-1">
                  {INCIDENT_ACTIONS_TAKEN.map(a => (
                    <label key={a} className="flex items-center gap-1.5 text-xs cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.actions_taken.includes(a)}
                        onChange={() => toggle("actions_taken", a)}
                        className="accent-rose-600 w-3.5 h-3.5"
                        data-testid={`incident-edit-action-${report.id}-${a.toLowerCase().replace(/\s+/g, '-')}`}
                      />
                      <span>{a}</span>
                    </label>
                  ))}
                </div>
              </div>

              {form.actions_taken.includes("Parent Contacted") && (
                <div>
                  <Label className="text-xs font-semibold">Parent Contact Detail</Label>
                  <Select value={form.parent_contact_detail || ""} onValueChange={(v) => setForm({ ...form, parent_contact_detail: v })}>
                    <SelectTrigger className="mt-1" data-testid={`incident-edit-parent-contact-${report.id}`}>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Left Message">Left Message</SelectItem>
                      <SelectItem value="Spoke">Spoke</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label className="text-xs font-semibold">Action Details</Label>
                <textarea
                  value={form.action_details}
                  onChange={(e) => setForm({ ...form, action_details: e.target.value })}
                  className="w-full min-h-[60px] mt-1 p-2 text-sm border rounded-lg focus:ring-2 focus:ring-rose-300 focus:border-rose-400 outline-none resize-y"
                  data-testid={`incident-edit-action-details-${report.id}`}
                />
              </div>

              <div>
                <Label className="text-xs font-semibold">Follow-Up</Label>
                <div className="flex flex-wrap gap-3 mt-1">
                  {INCIDENT_FOLLOWUP_OPTIONS.map(a => (
                    <label key={a} className="flex items-center gap-1.5 text-xs cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.followup.includes(a)}
                        onChange={() => toggle("followup", a)}
                        className="accent-rose-600 w-3.5 h-3.5"
                        data-testid={`incident-edit-followup-${report.id}-${a.toLowerCase().replace(/\s+/g, '-')}`}
                      />
                      <span>{a}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-xs font-semibold">Follow-Up Details</Label>
                <textarea
                  value={form.followup_details}
                  onChange={(e) => setForm({ ...form, followup_details: e.target.value })}
                  className="w-full min-h-[60px] mt-1 p-2 text-sm border rounded-lg focus:ring-2 focus:ring-rose-300 focus:border-rose-400 outline-none resize-y"
                  data-testid={`incident-edit-followup-details-${report.id}`}
                />
              </div>
            </>
          )}

          {form.great_day && (
            <div>
              <Label className="text-xs font-semibold">Great Day Notes</Label>
              <textarea
                value={form.great_day_notes}
                onChange={(e) => setForm({ ...form, great_day_notes: e.target.value })}
                className="w-full min-h-[60px] mt-1 p-2 text-sm border rounded-lg focus:ring-2 focus:ring-green-300 focus:border-green-400 outline-none resize-y"
                data-testid={`incident-edit-great-day-notes-${report.id}`}
              />
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 mt-4 pt-3 border-t">
          <Button variant="outline" size="sm" onClick={onClose} data-testid={`incident-edit-cancel-${report.id}`}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={save}
            disabled={saving}
            className="bg-rose-600 hover:bg-rose-700 text-white"
            data-testid={`incident-edit-save-${report.id}`}
          >
            <Save className="w-3.5 h-3.5 mr-1" />{saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function SupervisorNotesEditor({ report, token, onUpdate }) {
  const [notes, setNotes] = useState(Array.isArray(report.supervisor_notes) ? report.supervisor_notes : []);
  const [draft, setDraft] = useState("");
  const [pickedActions, setPickedActions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const toggleAction = (a) =>
    setPickedActions(prev => prev.includes(a) ? prev.filter(x => x !== a) : [...prev, a]);

  const handleSave = async () => {
    if (!draft.trim() && pickedActions.length === 0) return;
    setSaving(true);
    try {
      const res = await axios.patch(
        `${API}/incident-reports/${report.id}/supervisor-notes`,
        { note: draft.trim(), followup_actions: pickedActions },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const newNote = res.data?.note;
      const updatedNotes = newNote ? [...notes, newNote] : notes;
      setNotes(updatedNotes);
      setDraft("");
      setPickedActions([]);
      toast.success(newNote ? "Saved — note will show on each camper's snapshot" : "Saved");
      const mergedFollowup = Array.from(new Set([...(report.followup || []), ...(res.data?.actions_added || [])]));
      onUpdate?.({
        ...report,
        supervisor_notes: updatedNotes,
        followup: mergedFollowup,
        last_supervisor_update: newNote?.added_at,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save note");
    } finally {
      setSaving(false);
    }
  };

  const fmtTs = (iso) => {
    try { return new Date(iso).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
    catch { return iso; }
  };

  return (
    <div className="mt-3 border-t pt-3" data-testid={`sv-notes-editor-${report.id}`}>
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="flex items-center gap-1.5 text-xs font-semibold text-rose-700 hover:text-rose-900"
        data-testid={`sv-notes-toggle-${report.id}`}
      >
        <MessageSquare className="w-3.5 h-3.5" />
        Supervisor Follow-Up Notes
        {notes.length > 0 && (
          <Badge variant="secondary" className="ml-1 h-4 px-1.5 text-[10px]">{notes.length}</Badge>
        )}
        {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>
      {expanded && (
        <div className="mt-2 space-y-2">
          {notes.length > 0 && (
            <div className="space-y-1.5 max-h-[180px] overflow-y-auto pr-1" data-testid={`sv-notes-log-${report.id}`}>
              {notes.map((n, i) => (
                <div key={n.id || i} className="p-2 rounded-lg bg-rose-50/50 border border-rose-100">
                  <div className="flex items-center justify-between text-[10px] text-rose-700 mb-0.5">
                    <span className="font-semibold uppercase tracking-wide">{n.author_label || "Supervisor"}</span>
                    <span>{fmtTs(n.added_at)}</span>
                  </div>
                  <p className="text-xs text-rose-950 whitespace-pre-wrap">{n.text}</p>
                  {Array.isArray(n.actions) && n.actions.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {n.actions.map((a, j) => (
                        <Badge key={j} variant="outline" className="text-[9px] bg-white border-rose-200 text-rose-800">{a}</Badge>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-1.5 p-2 rounded-lg bg-rose-50/30 border border-rose-100">
            {SUPERVISOR_FOLLOWUP_ACTIONS.map(action => {
              const checked = pickedActions.includes(action);
              return (
                <label key={action} className="flex items-center gap-1.5 text-xs cursor-pointer">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleAction(action)}
                    className="accent-rose-600 w-3.5 h-3.5"
                    data-testid={`sv-action-${report.id}-${action.toLowerCase().replace(/[^a-z]/g, '-')}`}
                  />
                  <span className={checked ? "text-rose-900 font-medium" : "text-rose-700"}>{action}</span>
                </label>
              );
            })}
          </div>
          <textarea
            className="w-full min-h-[60px] p-2 text-sm border rounded-lg bg-white focus:ring-2 focus:ring-rose-300 focus:border-rose-400 outline-none resize-y"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Add a follow-up note (e.g., spoke with parents, talked to camper)..."
            data-testid={`sv-notes-textarea-${report.id}`}
          />
          <div className="flex items-center justify-between gap-2">
            <p className="text-[10px] text-muted-foreground">Note posts to each linked camper&apos;s snapshot under <em>Camper Notes</em>.</p>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={saving || (!draft.trim() && pickedActions.length === 0)}
              className="bg-rose-600 hover:bg-rose-700 text-white h-8 text-xs"
              data-testid={`sv-notes-save-${report.id}`}
            >
              <Save className="w-3 h-3 mr-1" />{saving ? "Saving..." : "Save Note"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}


function DivisionBroadcastBar({ token, divisionName }) {
  const [open, setOpen] = useState(false);
  const [audience, setAudience] = useState("parents");
  const [channel, setChannel] = useState("both");
  const [message, setMessage] = useState("");
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);

  const openModal = async (aud) => {
    setAudience(aud);
    setMessage("");
    setPreview(null);
    setOpen(true);
    try {
      const res = await axios.get(`${API}/supervisor/messages/broadcast/preview`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { audience: aud },
      });
      setPreview(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    }
  };

  const handleSend = async () => {
    if (!message.trim()) { toast.error("Message cannot be empty"); return; }
    const audLabel = audience === "parents" ? "ALL PARENTS" : "ALL HEAD COUNSELORS";
    const recipientCount = preview?.unique_recipients ?? "?";
    if (!window.confirm(`Send this message to ${audLabel} in ${divisionName} (~${recipientCount} recipients)?`)) return;
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/supervisor/messages/broadcast`,
        { audience, channel, message: message.trim(), confirm: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Sent to ${res.data.recipient_count} recipient${res.data.recipient_count !== 1 ? "s" : ""}`);
      setOpen(false);
      setMessage("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 p-3 rounded-xl border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
        <span className="text-xs font-semibold text-blue-900 uppercase tracking-wide mr-1">{divisionName} Broadcast:</span>
        <Button
          size="sm"
          variant="outline"
          onClick={() => openModal("parents")}
          className="bg-white border-blue-300 hover:bg-blue-100 text-blue-800 h-8 text-xs"
          data-testid="sv-broadcast-parents"
        >
          <Send className="w-3.5 h-3.5 mr-1" />Message All Parents
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => openModal("head_counselors")}
          className="bg-white border-blue-300 hover:bg-blue-100 text-blue-800 h-8 text-xs"
          data-testid="sv-broadcast-headcounselors"
        >
          <Send className="w-3.5 h-3.5 mr-1" />Message All Head Counselors
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Broadcast to {audience === "parents" ? "All Parents" : "All Head Counselors"} — {divisionName}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {preview ? (
              <div className="p-3 rounded-lg bg-slate-50 border text-xs space-y-1" data-testid="sv-broadcast-preview">
                <div><span className="font-semibold">Unique recipients:</span> {preview.unique_recipients}</div>
                <div><span className="font-semibold">SMS reachable:</span> {preview.sms_reachable}</div>
                <div><span className="font-semibold">Email reachable:</span> {preview.email_reachable}</div>
                <div><span className="font-semibold">Groups covered:</span> {(preview.groups || []).join(", ") || "—"}</div>
              </div>
            ) : (
              <div className="text-xs text-muted-foreground">Loading recipient counts…</div>
            )}
            <div>
              <Label className="text-xs">Channel</Label>
              <Select
                value={audience === "parents" ? "email" : channel}
                onValueChange={setChannel}
                disabled={audience === "parents"}
              >
                <SelectTrigger className="h-9 text-sm" data-testid="sv-broadcast-channel">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {/* Parents get email only — SMS to parents is policy-disabled
                      for supervisors. SMS is still available for head-counselor
                      broadcasts (internal staff comms). */}
                  {audience === "parents" ? (
                    <SelectItem value="email">Email only</SelectItem>
                  ) : (
                    <>
                      <SelectItem value="both">SMS + Email</SelectItem>
                      <SelectItem value="sms">SMS only</SelectItem>
                      <SelectItem value="email">Email only</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
              {audience === "parents" && (
                <p className="text-[10px] text-muted-foreground mt-1">
                  Parents receive email only.
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs">Message</Label>
              <textarea
                className="w-full min-h-[120px] p-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-300 focus:border-blue-400 outline-none resize-y"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={audience === "parents"
                  ? "Type your message to all parents in the division..."
                  : "Type your message to all head counselors in the division..."}
                data-testid="sv-broadcast-message"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                SMS includes the standard opt-out footer automatically.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={sending} data-testid="sv-broadcast-cancel">Cancel</Button>
            <Button
              onClick={handleSend}
              disabled={sending || !message.trim()}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="sv-broadcast-send"
            >
              <Send className="w-3.5 h-3.5 mr-1" />{sending ? "Sending..." : "Send Broadcast"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}


function SvIncidentNotes2025({ camperId, initialNotes, readOnly }) {
  const [allNotes, setAllNotes] = useState(initialNotes || "");
  const [newNote, setNewNote] = useState("");
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem("token");

  const handleAddNote = async () => {
    if (!newNote.trim() || readOnly) return;
    const dateStr = new Date().toLocaleDateString("en-US", { timeZone: "America/New_York", month: "2-digit", day: "2-digit", year: "numeric" });
    const entry = `[${dateStr}] ${newNote.trim()}`;
    const updated = allNotes ? `${allNotes}\n${entry}` : entry;
    setSaving(true);
    try {
      await axios.put(`${API}/campers/${camperId}/incident-notes-2025`, { notes: updated }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllNotes(updated);
      setNewNote("");
      toast.success("Note added");
    } catch {
      toast.error("Failed to save note");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteNote = async (lineIdx) => {
    if (readOnly) return;
    const lines = allNotes.split("\n").filter((_, i) => i !== lineIdx);
    const updated = lines.join("\n");
    try {
      await axios.put(`${API}/campers/${camperId}/incident-notes-2025`, { notes: updated }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllNotes(updated);
      toast.success("Note deleted");
    } catch {
      toast.error("Failed to delete note");
    }
  };

  return (
    <section data-testid="incident-notes-2025-section">
      <h3 className="font-heading font-semibold text-base flex items-center gap-2 mb-2 text-purple-700">
        <FileText className="w-4 h-4" />Camper Notes
        {readOnly && <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700 bg-amber-50 ml-1">Read Only</Badge>}
      </h3>
      {allNotes && (
        <div className="mb-2 p-2 rounded-lg border bg-purple-50/40 max-h-[160px] overflow-y-auto" data-testid="camper-notes-log">
          {allNotes.split("\n").filter(l => l.trim()).map((line, i) => (
            <div key={i} className="flex items-start gap-1 group py-0.5">
              <p className="text-sm flex-1">{line}</p>
              {!readOnly && (
                <button onClick={() => handleDeleteNote(i)} className="shrink-0 text-red-300 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity p-0.5" data-testid={`delete-note-${i}`} title="Delete note">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {!readOnly && (
        <div className="space-y-2">
          <textarea
            className="w-full min-h-[44px] max-h-[100px] p-2.5 text-sm border rounded-lg bg-white focus:ring-2 focus:ring-purple-300 focus:border-purple-400 outline-none resize-y"
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="Type a new note... (date added automatically)"
            data-testid="incident-notes-2025-textarea"
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleAddNote(); } }}
          />
          <Button size="sm" onClick={handleAddNote} disabled={saving || !newNote.trim()} className="w-full bg-purple-600 hover:bg-purple-700 text-xs h-8" data-testid="incident-notes-2025-save">
            <Save className="w-3 h-3 mr-1" />{saving ? "Saving..." : "Add Note"}
          </Button>
        </div>
      )}
      {!readOnly && <p className="text-[10px] text-muted-foreground mt-1">Press Enter to add. Date is stamped automatically.</p>}
    </section>
  );
}


function SupervisorColorWarView({ token }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/color-war/status`, { headers });
        setStatus(res.data);
      } catch { toast.error("Failed to load"); }
      setLoading(false);
    })();
  }, []);

  const groups = status?.groups || {};
  const sortedGroups = Object.values(groups).sort((a, b) => (a.group_code || "").localeCompare(b.group_code || ""));

  if (loading) return <div className="text-center py-10 text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-heading font-bold text-lg">Color War</h2>
        <p className="text-sm text-muted-foreground">
          {status?.assigned ? `${status.camper_count} campers + ${status.staff_count} staff assigned` : "No teams assigned yet"}
          {status?.assigned && <> — <span className="text-blue-600 font-medium">Blue: {status.blue_total}</span> | <span className="font-medium">White: {status.white_total}</span></>}
        </p>
      </div>
      <div className="space-y-2">
        {sortedGroups.map(g => (
          <Card key={g.group_code} className="overflow-hidden">
            <button
              className="w-full p-3 flex items-center justify-between text-left hover:bg-muted/50 transition-colors"
              onClick={() => setExpandedGroup(expandedGroup === g.group_code ? null : g.group_code)}
              data-testid={`sv-cw-group-${g.group_code}`}
            >
              <div className="flex items-center gap-2">
                <span className="font-semibold text-sm">{g.group_code}</span>
                {g.group_name && <span className="text-xs text-muted-foreground">— {g.group_name}</span>}
                <Badge variant="outline" className="text-[10px]">{g.campers.length + g.staff.length}</Badge>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-blue-600 font-medium">{g.campers.filter(c => c.color_war === "blue").length + g.staff.filter(s => s.color_war === "blue").length} blue</span>
                <span className="text-xs text-gray-600 font-medium">{g.campers.filter(c => c.color_war === "white").length + g.staff.filter(s => s.color_war === "white").length} white</span>
                {expandedGroup === g.group_code ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </div>
            </button>
            {expandedGroup === g.group_code && (
              <CardContent className="pt-0 pb-3 px-3">
                <div className="space-y-1">
                  {g.staff.map((s, i) => (
                    <div key={`staff-${i}`} className="flex items-center justify-between py-1 px-2 rounded bg-muted/30">
                      <span className="text-xs font-medium">{s.name} <span className="text-muted-foreground">(Staff)</span></span>
                      <Badge className={`text-[10px] ${s.color_war === "blue" ? "bg-blue-600" : "bg-gray-500"}`}>{s.color_war}</Badge>
                    </div>
                  ))}
                  {g.campers.map(c => (
                    <div key={c.id} className="flex items-center justify-between py-1 px-2 rounded">
                      <span className="text-xs">{c.name}</span>
                      <Badge className={`text-[10px] ${c.color_war === "blue" ? "bg-blue-600" : "bg-gray-500"}`}>{c.color_war}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}


function MarinerIntramuralsLanding({ token, onBack }) {
  const [view, setView] = useState("landing");
  const [subView, setSubView] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  if (view === "landing") {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-4">
          <Button variant="ghost" size="sm" onClick={onBack} className="px-2" data-testid="mariner-intramurals-back"><ArrowLeft className="w-4 h-4" /></Button>
          <div>
            <h2 className="text-lg font-bold">Mariner Intramurals</h2>
            <p className="text-xs text-muted-foreground">Read-only — Teams, Scores & Standings</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 max-w-lg">
          {[
            { id: "boys-teams", label: "Boys Teams", color: "blue", icon: Users },
            { id: "girls-teams", label: "Girls Teams", color: "rose", icon: Users },
            { id: "boys-scores", label: "Boys Scores", color: "emerald", icon: BarChart3 },
            { id: "girls-scores", label: "Girls Scores", color: "teal", icon: BarChart3 },
            { id: "playoffs", label: "Playoffs", color: "amber", icon: Swords },
            { id: "standings", label: "Final Standings", color: "violet", icon: Trophy },
          ].map(card => (
            <button key={card.id} onClick={() => { setView(card.id); setSubView(null); }}
              className={`flex flex-col items-center gap-2 p-4 rounded-2xl border-2 hover:shadow-lg transition-all active:scale-95 bg-${card.color}-50 border-${card.color}-200 hover:border-${card.color}-400`}
              data-testid={`mariner-intramurals-${card.id}`}
            >
              <card.icon className={`w-6 h-6 text-${card.color}-600`} />
              <span className={`text-xs font-bold text-${card.color}-800`}>{card.label}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (view === "boys-teams") return <TeamSorterView division="boys" headers={headers} onBack={() => setView("landing")} isEditable={false} />;
  if (view === "girls-teams") return <TeamSorterView division="girls" headers={headers} onBack={() => setView("landing")} isEditable={false} />;
  if (view === "boys-scores") return <ScorecardView headers={headers} onBack={() => setView("landing")} isEditable={false} defaultTab="boys" />;
  if (view === "girls-scores") return <ScorecardView headers={headers} onBack={() => setView("landing")} isEditable={false} defaultTab="girls" />;

  if (view === "playoffs") {
    if (!subView) {
      return (
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-4">
            <Button variant="ghost" size="sm" onClick={() => setView("landing")} className="px-2"><ArrowLeft className="w-4 h-4" /></Button>
            <h2 className="text-lg font-bold">Playoffs</h2>
          </div>
          <div className="grid grid-cols-2 gap-3 max-w-lg">
            {[
              { id: "boys", label: "Boys Playoffs", color: "blue" },
              { id: "girls", label: "Girls Playoffs", color: "rose" },
            ].map(card => (
              <button key={card.id} onClick={() => setSubView(card.id)}
                className={`flex flex-col items-center gap-2 p-4 rounded-2xl border-2 hover:shadow-lg transition-all bg-${card.color}-50 border-${card.color}-200 hover:border-${card.color}-400`}
              >
                <Swords className={`w-6 h-6 text-${card.color}-600`} />
                <span className={`text-xs font-bold text-${card.color}-800`}>{card.label}</span>
              </button>
            ))}
          </div>
        </div>
      );
    }
    return <PlayoffsView division={subView} headers={headers} onBack={() => setSubView(null)} isEditable={false} />;
  }

  if (view === "standings") return <FinalStandingsView headers={headers} onBack={() => setView("landing")} />;

  return null;
}

// ─── Navigator Supervisor: Daily Camper Present Totals card ───
function DailyAttendanceTotalsCard({ token, division, divisionLabel }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/supervisor/daily-attendance-totals?division=${division}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (alive) setData(res.data);
      } catch {
        if (alive) setData(null);
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    const interval = setInterval(load, 10000); // refresh every 10 seconds — feels instant once counselors finish
    return () => { alive = false; clearInterval(interval); };
  }, [token, division]);

  if (loading) return null;

  // ── Pre-launch / pre-attendance state ──
  // Show a non-clickable disclaimer card until: today is a camp day AND
  // every kid in every group has been marked (no `unmarked` remaining).
  const liveData = data && data.is_camp_day && data.total_expected > 0;

  if (!liveData) {
    return (
      <div
        className="w-full rounded-2xl border-2 border-dashed border-emerald-300 bg-emerald-50/60 p-4"
        data-testid={`daily-present-totals-card-placeholder-${division}`}
      >
        <div className="flex items-center gap-3">
          <CheckCircle className="w-7 h-7 text-emerald-500 flex-shrink-0 opacity-70" />
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wide text-emerald-700">Daily Camper Present Totals</p>
            <p className="text-sm text-emerald-900">
              This will show totals for the division and by group{" "}
              <strong>starting 6/29</strong> — the day <em>after</em> attendance saves each morning.
            </p>
            <p className="text-[11px] text-emerald-700 mt-0.5 opacity-80">{divisionLabel}</p>
          </div>
        </div>
      </div>
    );
  }

  const dateLabel = (() => {
    try {
      const [y, m, d] = (data.date || "").split("-").map(n => parseInt(n, 10));
      return `${m}/${d}/${y}`;
    } catch { return data.date; }
  })();

  const incompleteGroups = (data.groups || []).filter(g => g.expected > 0 && !g.complete);
  const totalUnmarked = incompleteGroups.reduce((s, g) => s + (g.unmarked || 0), 0);

  return (
    <button
      type="button"
      onClick={() => setExpanded(v => !v)}
      className="w-full text-left rounded-2xl border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 to-green-50 hover:from-emerald-100 hover:to-green-100 hover:border-emerald-400 transition-all p-4 shadow-sm hover:shadow-md"
      data-testid={`daily-present-totals-card-${division}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <CheckCircle className="w-7 h-7 text-emerald-600 flex-shrink-0" />
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wide text-emerald-700">Daily Camper Present Totals</p>
            <p className="text-sm text-emerald-900 font-semibold">
              Total campers for today <span className="font-mono">{dateLabel}</span>:{" "}
              <span className="text-2xl font-extrabold tracking-tight ml-1" data-testid={`daily-total-count-${division}`}>{data.total_present}</span>
              <span className="text-xs font-medium text-emerald-700 ml-1">/ {data.total_expected} expected</span>
            </p>
            {incompleteGroups.length > 0 && (
              <p className="text-[11px] font-semibold text-amber-700 mt-0.5" data-testid={`daily-totals-incomplete-note-${division}`}>
                Still marking: {incompleteGroups.length} group{incompleteGroups.length !== 1 ? "s" : ""} · {totalUnmarked} camper{totalUnmarked !== 1 ? "s" : ""} unmarked — total will rise as they finish
              </p>
            )}
          </div>
        </div>
        {expanded ? <ChevronUp className="w-5 h-5 text-emerald-700" /> : <ChevronDown className="w-5 h-5 text-emerald-700" />}
      </div>

      {expanded && (
        <div className="mt-4 pt-4 border-t border-emerald-200 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid={`daily-totals-breakdown-${division}`}>
          {(data.groups || []).map(g => (
            <div
              key={g.group_code}
              className="rounded-lg bg-white border border-emerald-200 p-2.5 text-center"
              data-testid={`daily-total-${g.group_code}`}
            >
              <p className="text-[10px] font-bold uppercase text-emerald-700">{g.group_code}</p>
              <p className="text-xl font-extrabold text-emerald-800 leading-tight">{g.here_today}</p>
              <p className="text-[9px] text-emerald-600">of {g.expected} present</p>
              {g.absent > 0 && (
                <p className="text-[9px] text-rose-600 font-semibold">{g.absent} absent</p>
              )}
              {g.unmarked > 0 && (
                <p className="text-[9px] text-amber-600 font-semibold">{g.unmarked} unmarked</p>
              )}
            </div>
          ))}
        </div>
      )}
      <p className="text-[10px] text-emerald-700 mt-2 opacity-80">
        {expanded ? "Tap to collapse" : "Tap to see per-group breakdown"} · {divisionLabel}
      </p>
    </button>
  );
}
